import moment from 'moment';
import {RatecardDetails, SearchCompanyByABNResult} from './domain/application-service.domain';
import {calculateBusinessOverdraftVariationFees, isBusinessOverdraftVariationPurpose} from './utils/business-overdraft-fee-calculator';
import {
  AssetFinanceTier1,
  AssetFinanceTier1Range,
  AssetFinanceTier2,
  AssetFinanceTier2Range,
  AssetFinanceTier3,
  AssetFinanceTier3Range,
  AssetFinanceTier4,
} from './domain/admin-service.domain';
import {parseJSON} from './utils/json';
import {
  ASSET_FINANCE_BROKERAGE_UPLIFT_STEP_PERCENT,
  ASSET_FINANCE_BROKERAGE_UPLIFT_TIER1_PERCENT,
  ASSET_FINANCE_BROKERAGE_UPLIFT_TIER2_PERCENT,
  BUSINESS_OVERDRAFT_ADDITIONAL_COMMISSION_BROKERAGE_UPLIFT_PERCENT,
  BUSINESS_OVERDRAFT_ADDITIONAL_COMMISSION_RATE_UPLIFT_PERCENT,
  BUSINESS_OVERDRAFT_BASE_BROKERAGE_PERCENT,
  isAssetCars,
  isAssetNewCars,
  isAssetOldCars, isAssetTypeAnyTruck,
  isAssetTypeCars,
  isAssetTypeLightOrHeavyTruck,
  isAssetVehicles,
  TIER1_ASSET_CATS,
  TIER2_ASSET_CATS,
  TIER3_ASSET_CATS,
  TIER4_ASSET_CATS,
} from './const';
import _ from 'lodash';
import {Moment} from 'moment/moment';
import {PaymentFrequencyType, PaymentFrequencyValue} from './domain/component/payment-frequency-selection-component.domain';
import {AssetConditionType, AssetConditionValue} from './domain/component/asset-condition-component.domain';
import {LoanTermType, LoanTermValue} from './domain/component/loan-terms-selection-component.domain';
import {FinanceType, FinanceTypeValue} from './domain/component/finance-type-component.domain';
import {BalloonPaymentValue} from './domain/component/balloon-payment-component.domain';
import {BrokerageSelectionType, BrokerageSelectionValue} from './domain/component/brokerage-selection-component.domain';
import {BusinessSearchValue} from './domain/component/business-search-component.domain';
import {TotalPaymentBreakupDialogData} from './domain/component/total-payment-breakup-dialog.domain';
import numeral from 'numeral';
import {AssetCategoryTypeSelectionValue} from './domain/component/asset-category-type-selection-component.domain';
import {TransactionType} from './domain/component/transaction-type-component.domain';
import {TruckGrossVehicleWeight, TruckGrossVehicleWeightSelection} from './domain/redbook-service.domain';
import {
  AppCalculatorAssetFinanceData,
  AppCalculatorBusinessLoanData,
  AppCalculatorBusinessOverdraftData,
  CalculateAssetFinanceEstmationResult,
  CalculateBusinessLoanEstimationResult,
  CalculateBusinessOverdraftEstimationResult
} from "./domain/app-calculator.domain";
import {normaliseFromMonthyAmount} from "./utils/utils";

export type RepaymentFrequencyType = 'Weekly' | 'Monthly' | 'Fortnightly';
export type RepaymentType = 'arrears' | 'advance';
export type MonthType = 'Jan'| 'Feb'| 'Mar'| 'Apr'| 'May'| 'Jun'| 'Jul'| 'Aug'| 'Sept'| 'Oct'| 'Nov'| 'Dec';

export type Operator = '<' | '>=' | '-' | '>' | '<=';

// An Asset Finance rate card cell may hold "+N": the previous cell's rate plus N percentage points.
export const isRelativeRate = (rate: string | null | undefined): boolean =>
  typeof rate === 'string' && rate.trim().startsWith('+');

// rates are ordered (New, then age buckets oldest-last); a relative rate resolves against the one before it.
export const resolveRelativeRates = (rates: (string | null | undefined)[]): number[] => {
  const resolved: number[] = [];
  rates.forEach((rate, i) => {
    const n = parseFloat(rate ?? '0');
    if (isRelativeRate(rate)) {
      if (i === 0) {
        throw Error(`Rate card: the New rate cannot be relative (${rate})`);
      }
      resolved.push(_.round(resolved[i - 1] + n, 2));
    } else {
      resolved.push(n);
    }
  });
  return resolved;
}

const resolveAssetFinanceTierRates = (range: AssetFinanceTier1Range | AssetFinanceTier2Range | AssetFinanceTier3Range | undefined) => {
  const bucketRate = (type: 'Low' | 'Medium' | 'High') => range?.YearRanges?.find(r => r.YearRangeType === type)?.Rate;
  const [New, Low, Medium, High] = resolveRelativeRates([range?.New?.Rate ?? '0', bucketRate('Low'), bucketRate('Medium'), bucketRate('High')]);
  return {New, Low, Medium, High};
}

export const parseAssetFinanceTier = <T extends {Threshold: string, Operator: Operator}>(d: number, arr: T[]) => {
  const r = (arr ?? []).reduce((acc: T | null, curr: T | null) => {
    const threshold = curr!.Threshold;
    const operator = curr!.Operator;
    switch (operator) {
      case '-': {
        const t: number[] = threshold.split('-').map(_t => parseInt(_t, 10));
        if (d >= t[0] && d <= t[1]) {
          return curr;
        }
        break;
      }
      case '<=': {
        const t = parseInt(threshold, 10);
        if (d <= t) {
          return curr;
        }
        break;
      }
      case '<': {
        const t = parseInt(threshold, 10);
        if (d < t) {
          return curr;
        }
        break;
      }
      case '>=': {
        const t = parseInt(threshold, 10);
        if (d >= t) {
          return curr;
        }
        break;
      }
      case '>': {
        const t = parseInt(threshold, 10);
        if (d > t) {
          return curr;
        }
        break;
      }
      default:
        throw Error(`unexpected operator ${operator} when parsing Asset Finance Tiers`);
    }
    return acc;
  }, null);
  return r;
};

/**
 * ZEN-818 follow-up: compares the stored Equifax Director Score threshold
 * (e.g. '>=700', '<600', null) against a required floor expressed as '>=N'.
 *
 * Returns true iff the stored threshold guarantees the director score is ≥ N.
 *   thresholdMeets('>=700', '>=700') → true
 *   thresholdMeets('>=700', '>=600') → true
 *   thresholdMeets('>=600', '>=700') → false
 *   thresholdMeets('<700',  '>=600') → false   (conservative — unknown)
 *   thresholdMeets(null,    '>=600') → false
 */
export const thresholdMeets = (stored: string | null | undefined, required: '>=500' | '>=550' | '>=600' | '>=700'): boolean => {
  if (!stored) return false;
  const m = /^(>=|<)(\d+)$/.exec(stored);
  if (!m) return false;
  const op = m[1];
  const storedFloor = Number(m[2]);
  const requiredFloor = Number(required.slice(2));
  if (op === '>=') {
    return storedFloor >= requiredFloor;
  }
  // '<N' strictly contradicts any >=requiredFloor when N <= requiredFloor
  // and provides no positive guarantee otherwise.
  return false;
};

/** Stored PricingDetails.EquifaxDirectorScoreThreshold for an Asset Finance yes/no pair. */
export const assetFinanceEquifaxDirectorScoreThreshold = (
  above700: boolean | null | undefined,
  above550: boolean | null | undefined,
): '>=700' | '>=550' | '<550' => {
  if (above700) return '>=700';
  if (above550) return '>=550';
  return '<550';
};

const premiumDiscountPercent = (value: number | string | null | undefined): number => {
  if (value == null || String(value).trim() === '') return 0;
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export type BusinessTierProduct = 'BusinessLoans' | 'BusinessOverdraft';
export type BusinessTier = 1 | 2 | 3;

/**
 * ZEN-818 follow-up: resolves the tier (and rate) for Business Loan / Business Overdraft
 * pricing per the 3-tier model.
 *
 * Minimum standard (enforced by the credit flow's BLOD_MIN_STANDARDS decline): EF Director
 * score >= 550. Any quote that reaches this resolver is therefore assumed to meet the 550
 * floor — we only distinguish between ≥700 (T1-eligible) and <700 (T2/T3).
 *
 * Tier 1 (premium rate) is assessed on the entity's own GST age (gstAgeMonths) —
 * business continuity from a prior entity does not earn the premium rate. Tier 2/3
 * carry no GST age or director score checks of their own; minimum standards are
 * enforced separately by the credit flow (BLOD_MIN_STANDARDS).
 *
 * Tier 1 and Tier 2 are both property-backed rates, so a non-property-owner can only
 * ever reach Tier 3 regardless of GST age or director score.
 *
 * Business Loan & Overdraft:
 *   T1: PropertyOwner & GSTAge >= 60 & EFDirector >= 700
 *   T2: PropertyOwner
 *   T3: default (everything else, including all non-property-owners)
 */
export const resolveBusinessTierRate = (
  product: BusinessTierProduct,
  form: FormDataForBusinessFinance | FormDataForBusinessOverdraft,
  rateCard: RatecardDetails,
): { tier: BusinessTier; rate: number } => {
  const tiers = rateCard.Tiers ?? { tier1: 0, tier2: 0, tier3: 0 };
  const gstAge = form.gstAgeMonths ?? 0;
  const ef700 = !!form.equifaxDirectorScoreAbove700;
  const propertyOwner = !!form.propertyOwner;

  // Tier rates can arrive from the rate card as strings (e.g. "8.50"); coerce to a
  // number so downstream additive arithmetic doesn't string-concatenate the rate.
  const toRate = (value: number | string | null | undefined): number => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const t1TibMin = 60;

  if (propertyOwner && gstAge >= t1TibMin && ef700) {
    return { tier: 1, rate: toRate(tiers.tier1) };
  }
  if (propertyOwner) {
    return { tier: 2, rate: toRate(tiers.tier2) };
  }
  return { tier: 3, rate: toRate(tiers.tier3) };
};

export class AppCalculator {

  isAssetVehicles = isAssetVehicles;
  isAssetCars = isAssetCars;
  isAssetNewCars = isAssetNewCars;
  isAssetOldCars = isAssetOldCars;
  isAssetTypeCars = isAssetTypeCars;

  isAssetTypeLightOrHeavyTruck = isAssetTypeLightOrHeavyTruck;

  repaymentFrequency: RepaymentFrequencyType = 'Fortnightly';
  repaymentFrequencyPeriod: 26 | 52 | 12 = 26;
  principalValue = 15000;
  interestValue = 5;
  baseRate = 3; // for consumer
  percentagePaidToDealerOrBroker = 1; // for consumer
  monthlyAccountKeepingFee = 0; // for consumer
  loanValue = 24;
  dateValue: Date = new Date(moment().add(1, 'days').format('MM/DD/YYYY'));
  acceptQuotation = false;
  residualPercent = 0;
  residualAmount = 0;
  brokeragePercent = 0;
  brokerageAmount = 0;
  brokerOriginationFee = 0;
  repaymentType: RepaymentType = 'advance';
  emi = 0;
  amountForComparisonRate = 15000; // for consumer;
  emiOnBaseRate = 0; // for consumer;
  emiOnBaseRateAmt = 0; // for consumer;
  pvOfDifferenceOfRepayments = 0; // for consumer;
  commission = 0; // for consumer;
  totalEarned = 0; // consumer
  totalEarnedInclGst = 0; // consumer
  amountToUs = 0; // for consumer;
  emiAmtPlusMonthlyAccountKeepingFee = 0; // for consumer
  princ = 0;
  tent = 0;
  dataUnits: DataUnitType[] = [];
  yearWiseData: YearWiseData[] = [];
  quarterlyData: MonthWiseData[] = [];
  totalInterest = 0;
  totalAmount = 0;
  totalPrincipal = 0;
  totalPrincipalYear = 0;
  totalInterestYear = 0;
  emiAmt = 0;
  interestAmt = 0;
  totalAmt = 0;
  principalAmt = 0;
  dateObj: Date = new Date();
  endBalance = 0;
  yearTotal = 0;
  monthNames: MonthType[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  inter = 0;
  quarterlyTotal = 0;
  displayedInterest = 0;
  comparisonRate = 0;

  constructor() {
    this.setInitValues();
  }

  setPrinicipalValue(value: any): any {
    this.principalValue = value;
  }

  setAmountForComparisonRate(value: any): any {
    this.amountForComparisonRate = value;
  }

  setInterestValue(value: any): any {
    this.interestValue = value;
  }

  setBaseRate(value: any): any {
    this.baseRate = value
  }

  setloanValue(value: any): any {
    this.loanValue = value;
  }

  setRepaymentFrequency(value: RepaymentFrequencyType): any {
    this.repaymentFrequency = value;
  }

  setAcceptQuotation(value: any) {
    this.acceptQuotation = value;
  }

  setResidualPercent(value: any) {
    this.residualPercent = value;
  }

  setRepaymentType(value: any) {
    this.repaymentType = value;
  }

  setResidualAmount(value: any) {
    this.residualAmount = value;
  }

  setBrokeragePercent(value: any) {
    this.brokeragePercent = value;
  }

  setBrokerageAmount(value: any) {
    this.brokerageAmount = value;
  }

  setDateValue(value: string) {
    this.dateValue = moment(value, 'DD/MM/YYYY').toDate();
  }

  setBrokerOriginationFee(value: any) {
    this.brokerOriginationFee = value;
  }

  setPercentagePaidToDealerOrBroker(value: number) {
    this.percentagePaidToDealerOrBroker = (_.round((value == undefined ? 100 : value) / 100, 2));
  }

  setMonthlyAccountKeepingFee(value: any) {
    this.monthlyAccountKeepingFee = value;
  }

  setRepaymentFrequencyPeriod() {
    if (this.repaymentFrequency == 'Fortnightly') {
      this.repaymentFrequencyPeriod = 26;
    } else if (this.repaymentFrequency == 'Weekly') {
      this.repaymentFrequencyPeriod = 52;
    } else if (this.repaymentFrequency == 'Monthly') {
      this.repaymentFrequencyPeriod = 12;
    }
  }

  public setInitValues(): void {
    this.setRepaymentFrequencyPeriod();
    this.emi = this.calculateEMI();
    this.princ = this.principalValue + this.brokerageAmount; // emi and all should be count based on this
    this.tent = this.loanValue / 12 * this.repaymentFrequencyPeriod;
    this.dataUnits = [];
    this.yearWiseData = [];
    this.dateObj = new Date(this.dateValue.getTime());
    this.totalInterest = 0;
    this.totalAmount = 0;
    this.totalPrincipal = 0;
    this.totalPrincipalYear = 0;
    this.totalInterestYear = 0;
    this.emiAmt = this.getCurrencyVal(this.tent ? Math.round(this.emi * 100) / 100 : 0);
    this.totalAmt = Math.round(((this.emiAmt * this.tent)) * 100) / 100 + this.residualAmount;
    this.interestAmt = this.getCurrencyVal(this.tent ? Math.abs(this.residualAmount) + Math.round(((this.emiAmt * this.tent) - this.princ) * 100) / 100 : 0);
    /* princ to include principal value + brokerage amount */
    this.principalAmt = this.principalValue; // this value is just displayed in UI, no use in calculations

    // for consumer
    this.emiOnBaseRate = this.calculateEMIOnBaseRate();
    this.emiOnBaseRateAmt = _.round(this.emiOnBaseRate, 2)
    this.pvOfDifferenceOfRepayments = _.round(- this.PV(
      this.baseRate / ((this.tent / this.loanValue) * 1200),
      this.tent,
      this.PMT(
        this.interestValue / ((this.tent / this.loanValue) * 1200),
        this.tent,
        - this.principalValue,
        0,
        this.repaymentType == 'arrears' ? 0 : 1,
        ),
        0,
        this.repaymentType == 'arrears' ? 0 : 1,
      ) - this.principalValue, 2);
    this.commission = _.round(this.pvOfDifferenceOfRepayments * this.percentagePaidToDealerOrBroker, 2);
    console.log('this.pvOfDifferenceOfRepayments: ',this.pvOfDifferenceOfRepayments)
    console.log('this.percentagePaidToDealerOrBroker: ', this.percentagePaidToDealerOrBroker)
    this.totalEarned = _.round(this.commission + this.brokerOriginationFee, 2);
    this.totalEarnedInclGst = _.round(this.totalEarned * 1.1, 2);
    this.amountToUs = _.round(this.pvOfDifferenceOfRepayments - this.commission, 2);
    this.displayedInterest = this.interestValue;
    this.comparisonRate = _.round((this.RATE(this.tent, - this.emiAmt - this.monthlyAccountKeepingFee, this.amountForComparisonRate, - this.residualAmount, this.repaymentType == 'arrears' ? 0 : 1)) * (this.tent / this.loanValue) * 1200, 2);
    this.emiAmtPlusMonthlyAccountKeepingFee = _.round(this.emiAmt + this.getAccountKeepingFeeBasedOnPaymentFrequency(), 2);
    if (this.monthlyAccountKeepingFee) {
      this.totalAmt = Math.round(((this.emiAmtPlusMonthlyAccountKeepingFee * this.tent)) * 100) / 100 + this.residualAmount;
    }
  }

  getAccountKeepingFeeBasedOnPaymentFrequency() {
    const monthlyAccountKeepingFee = normaliseFromMonthyAmount(this.monthlyAccountKeepingFee, this.repaymentFrequency);
    return monthlyAccountKeepingFee;
  }


  /* Balloon payment restriction -> max settings
    * Per product guide (Feb 2026):
    *
    * Tier 1/2:  <=24m: 50%, <=36m: 40%, <=48m: 35%, <=60m: 30%, >60m: 0%
    * Tier 3:    <=24m: 20%, <=36m: 10%, <=48m: 10%, >48m: 0%
    * Tier 4:    always 0%
    * Assets above 12 years EOT: 0%
    * Loan terms > 60 months: 0% for all tiers
  */
  getMaxRV(assetCat: string, loanterms: number, assetYear: number, assetType: string = ''): number {
    const eot = (moment().year() - assetYear - 1) + (loanterms / 12);
    let isAssetEligibleforRV = false;
    let maxRV = 0;

    if (loanterms > 60) return 0;

    // consumer asset
    if (parseInt(assetCat) === 139) {
      if (!(eot > 12 || loanterms / 12 > 5 || parseInt(assetType) === 4)) {
        isAssetEligibleforRV = true;
      }
      if (isAssetEligibleforRV) {
        if (loanterms / 12 > 4) {
          maxRV = 35;
        } else if (loanterms / 12 > 3) {
          maxRV = 45;
        } else {
          maxRV = 50;
        }
      }
    } else {
      const catNum = parseInt(assetCat);

      // Tier 1 & 2
      if ([130, 132, 137, 140].includes(catNum)) {
        if (eot <= 12) {
          isAssetEligibleforRV = true;
        }
        if (isAssetEligibleforRV) {
          if ((loanterms / 12) == 2) {
            maxRV = Math.ceil((- (5 / 12) * loanterms + 60) / 5) * 5;
          } else {
            maxRV = Math.ceil((- (5 / 12) * loanterms + 55) / 5) * 5;
          }
        }
      }

      // Tier 3 (including Solar 106) — same balloon schedule per CRDL62
      if ([136, 133, 106].includes(catNum)) {
        if (eot <= 12) {
          isAssetEligibleforRV = true;
        }
        if (isAssetEligibleforRV) {
          const termYears = loanterms / 12;
          if (termYears <= 2) {
            maxRV = 20;
          } else if (termYears <= 4) {
            maxRV = 10;
          } else {
            maxRV = 0;
          }
        }
      }

      // Tier 4 (134, 135, 142): always 0% — no action needed
    }

    console.log(`*** maxRv for eligibleForRv ${isAssetEligibleforRV} eot ${eot} assetCat ${assetCat}, loanTerms ${loanterms}, assetYear ${assetYear} = ${maxRV}`);
    return maxRV;
  }


  public calculateEMI(): number {
    const interestValue: number = this.getInterest();
    const tent: number = this.loanValue / 12 * this.repaymentFrequencyPeriod;
    let frate: any = 0;
    let nper: any;
    const pv: any = this.principalValue + this.brokerageAmount;
    const fv: number = -this.residualAmount;
    const type: any = this.repaymentType == 'arrears' ? 0 : 1;
    nper = this.repaymentFrequencyPeriod * (this.loanValue / 12);
    frate = parseFloat((this.interestValue / (this.repaymentFrequencyPeriod * 100)).toString());
    let totalRepayment = this.PMT(frate, nper, pv, fv, type);
    totalRepayment = -totalRepayment.toFixed(2);
    return totalRepayment;
  }

  public calculateEMIOnBaseRate(): number {
    let frate: any = 0;
    let nper: any;
    const pv: any = this.principalValue + this.brokerageAmount;
    const fv: number = -this.residualAmount;
    const type: any = this.repaymentType == 'arrears' ? 0 : 1;
    nper = this.repaymentFrequencyPeriod * (this.loanValue / 12);
    frate = parseFloat((this.baseRate / (this.repaymentFrequencyPeriod * 100)).toString());
    let totalRepayment = this.PMT(frate, nper, pv, fv, type);
    totalRepayment = -totalRepayment.toFixed(2);
    return totalRepayment;
  }

  /*
    Count Rate given period payment
    nper: total no of repayments 2(Years) * Based on frequency period( weekly - 52, fortnightly-26, monthly-12)
    pmt: period payment
    pv: present value (loan amount + docfee + brokerage amount)
    fv: -(residual amount Or ballon payment)
    type: 0 for Business Term Loan, 1 for others
  */
  RATE(nper: number, pmt: number, pv: number, fv: number, type: number, guess: number = 0.01) {
    // Sets default values for missing parameters
    fv = typeof fv !== 'undefined' ? fv : 0;
    type = typeof type !== 'undefined' ? type : 0;
    guess = typeof guess !== 'undefined' ? guess : 0.1;
    // Sets the limits for possible guesses to any
    // number between 0% and 100%
    let lowLimit = 0;
    let highLimit = 1;
    // Defines a tolerance of up to +/- 0.00005% of pmt, to accept
    // the solution as valid.
    let tolerance = Math.abs(0.00000005 * pmt);
    // Tries at most 40 times to find a solution within the tolerance.
    for (let i = 0; i < 40; i++) {
      // Resets the balance to the original pv.
      let balance = pv;
      // Calculates the balance at the end of the loan, based
      // on loan conditions.
      for (let j = 0; j < nper; j++ ) {
        if (type == 0) {
          // Interests applied before payment
          balance = balance * (1 + guess) + pmt;
        } else {
          // Payments applied before insterests
          balance = (balance + pmt) * (1 + guess);
        }
      }
      // Returns the guess if balance is within tolerance.  If not, adjusts
      // the limits and starts with a new guess.
      if (Math.abs(balance + fv) < tolerance) {
        return guess;
      } else if (balance + fv > 0) {
        // Sets a new highLimit knowing that
        // the current guess was too big.
        highLimit = guess;
      } else  {
        // Sets a new lowLimit knowing that
        // the current guess was too small.
        lowLimit = guess;
      }
      // Calculates the new guess.
      guess = (highLimit + lowLimit) / 2;
    }
    // Returns null if no acceptable result was found after 40 tries.
    return 0;
  }

  /*
      Calculations reference : https://github.com/sutoiku/formula.js/blob/master/lib/financial.js

      rate: (interest / 100) * Based on frequency period( weekly - 52, fortnightly-26, monthly-12)
      nper: total no of repayments 2(Years) * Based on frequency period( weekly - 52, fortnightly-26, monthly-12)
      pv: present value (loan amount + docfee + brokerage amount)
      fv: -(residual amount Or ballon payment)
      type: 0 for Business Term Loan, 1 for others
  */

  PMT(rate: number, nper: number, pv: number, fv: number, type: 0 | 1) {
    if (!fv) { fv = 0; }
    if (!type) { type = 0; }
    let pmt = 0;
    if (rate == 0) { return -(pv + fv) / nper; }

    const pvif = Math.pow(1 + rate, nper);
    pmt = rate / (pvif - 1) * -(pv * pvif + fv);

    if (type == 1) {
      pmt /= (1 + rate);
    }

    return pmt;
  }


  /*
      count interest given on particular payment
      period: no(index) of repayment to calculate interest for
      periods: total no.of periods
      present: pv
      future: rv
  */
  IPMT(rate: number, period: number, periods: number, present: number, future: number, type: 0 | 1) {
    // Credits: algorithm inspired by Apache OpenOffice
    future = future || 0;
    type = type || 0;
    // Compute payment
    const payment = this.PMT(rate, periods, present, future, type);

    // Compute interest
    let interest;
    if (period === 1) {
      if (type === 1) {
        interest = 0;
      } else {
        interest = -present;
      }
    } else {
      if (type === 1) {
        interest = this.FV(rate, period - 2, payment, present, 1) - payment;
      } else {
        interest = this.FV(rate, period - 1, payment, present, 0);
      }
    }

    // Return interest
    return interest * rate;
  }


  PPMT(rate: number, period: number, periods: number, present: number, future: number, type: 0 | 1) {
    future = future || 0;
    type = type || 0;
    return this.PMT(rate, periods, present, future, type) - this.IPMT(rate, period, periods, present, future, type);
  }

  FV(rate: number, periods: number, payment: number, value: number, type: number) {
    value = value || 0;
    type = type || 0;

    // Return future value
    let result;
    if (rate === 0) {
      result = value + payment * periods;
    } else {
      const term = Math.pow(1 + rate, periods);
      if (type === 1) {
        result = value * term + payment * (1 + rate) * (term - 1) / rate;
      } else {
        result = value * term + payment * (term - 1) / rate;
      }
    }
    return -result;
  }

  PV(rate: number, num: number, amount: number, remain: number, type: 0 | 1) {
    let factor;

    if (type == 0) {
      factor = (rate == 0 ? num : (1 - Math.pow(1+rate, -num))/rate);
    } else {
      factor = (rate == 0 ? num : (1 - Math.pow(1+rate, -num))*(1+rate)/rate);
    }

    return -amount*factor - remain * Math.pow(1+rate, -num);
  }


  public refreshUI(): void {
    this.setInitValues();
    this.calRangeValues();
  }

  public calRangeValues(): void {
    const previousYear = null;
    const currentYear = null;
    const yearBasedData: any = {};
    let frate: any = 0;
    let nper: any;
    const pv: any = this.principalValue + this.brokerageAmount;
    const fv: number = -this.residualAmount;
    const type: any = this.repaymentType == 'arrears' ? 0 : 1;
    let princ: any = pv;
    nper = this.repaymentFrequencyPeriod * (this.loanValue / 12);
    frate = parseFloat((this.interestValue / (this.repaymentFrequencyPeriod * 100)).toString());
    for (let i = 0; i < this.tent; i++) {
      const ppmt = -this.PPMT(frate, i + 1, nper, pv, fv, type);
      this.inter = this.emi - ppmt;
      this.totalInterest += this.inter;
      this.totalAmount += this.emi;
      this.totalPrincipal += ppmt;
      this.endBalance = princ - ppmt;
      this.yearTotal += this.emi;
      this.totalPrincipalYear += ppmt;
      this.totalInterestYear += this.inter;
      this.quarterlyTotal += this.emi;
      const currentMonthNumber = this.dateObj.getMonth() + 1;
      const dataUnit = {
        month: this.monthNames[this.dateObj.getMonth()],
        index: (i + 1),
        totalInterest: Math.round(this.totalInterest * 100) / 100,
        totalAmount: this.totalAmount,
        // emi: (i == this.tent-1) ? (Math.round((this.emi+this.residualAmount)*100)/100) : (Math.round(this.emi*100)/100),
        emi: Math.round(this.emi * 100) / 100,
        year: this.dateObj.getFullYear(),
        beginningBalance: Math.round(princ * 100) / 100,
        interest: Math.round(this.inter * 100) / 100,
        pricipalPaid: Math.round(ppmt * 100) / 100,
        endingBalance: Math.round(this.endBalance * 100) / 100,
        date: this.dateObj.getFullYear() + '-' + currentMonthNumber + '-' + this.dateObj.getDate()
      };
      this.dataUnits.push(dataUnit);
      if (yearBasedData[this.dateObj.getFullYear()] == undefined) {
        yearBasedData[this.dateObj.getFullYear()] = [];
      }
      yearBasedData[this.dateObj.getFullYear()].push(dataUnit);

      princ = this.endBalance;
      if (i < this.tent - 1) {
        if (this.repaymentFrequency == 'Weekly') {
          this.dateObj.setDate(this.dateObj.getDate() + 7);
        } else if (this.repaymentFrequency == 'Monthly') {
          this.dateObj.setMonth(this.dateObj.getMonth() + 1);
        } else {
          this.dateObj.setDate(this.dateObj.getDate() + 2 * 7);
        }
      }
    }

    /* Add last payment for residual value (Balloon Payment) */
    /*
        rate: same as other cal
        nper: 1 - fixed
        per: 1 - fixed
        pv: last end balance got at the end of payments
        type: will always default to arrears when counting residual last payment
    */
    if (this.residualAmount > 0) {
      const endBalance = this.dataUnits[this.dataUnits.length - 1].endingBalance;

      const lastIPMT = -this.IPMT(frate, 1, 1, endBalance, 0, 0);
      const lastRepayment = -fv;
      const lastPPMT = lastRepayment - lastIPMT;
      if (this.repaymentFrequency == 'Weekly') {
        this.dateObj.setDate(this.dateObj.getDate() + 7);
      } else if (this.repaymentFrequency == 'Monthly') {
        this.dateObj.setMonth(this.dateObj.getMonth() + 1);
      } else {
        this.dateObj.setDate(this.dateObj.getDate() + 2 * 7);
      }
      const lastMonthNumber = this.dateObj.getMonth() + 1;
      const lastDataUnit = {
        month: this.monthNames[this.dateObj.getMonth() + 1],
        index: this.tent,
        totalInterest: Math.round((this.totalInterest + lastIPMT) * 100) / 100,
        totalAmount: this.totalAmount,
        emi: lastRepayment,
        year: this.dateObj.getFullYear(),
        beginningBalance: Math.round(princ * 100) / 100,
        interest: Math.round(lastIPMT * 100) / 100,
        pricipalPaid: Math.round(lastPPMT * 100) / 100,
        endingBalance: Math.round((endBalance - lastPPMT) * 100) / 100,
        date: this.dateObj.getFullYear() + '-' + lastMonthNumber + '-' + this.dateObj.getDate()
      };
      this.dataUnits.push(lastDataUnit);
      if (yearBasedData[this.dateObj.getFullYear()] == undefined) {
        yearBasedData[this.dateObj.getFullYear()] = [];
      }
      yearBasedData[this.dateObj.getFullYear()].push(lastDataUnit);
    }
    /* Add last payment for residual value (Balloon Payment) */

    /* prepare yearwise data */
    let beginBalance = 0;
    const yprinc = this.principalValue + this.brokerageAmount;
    let totalInterest = 0;
    let totalPrincipal = 0;
    let totalAmount = 0;
    let endingBalance = 0;
    let yearTotal = 0;
    let yearN: any = null;
    let year: any = null;
    let totalPrincipalYear = 0;
    let totalInterestYear = 0;
    for (const property in yearBasedData) {
      if (yearBasedData.hasOwnProperty(property)) {
        const yearData = yearBasedData[property];
        beginBalance = yearData[0].beginningBalance;
        for (let monthC = 0; monthC <= yearData.length - 1; monthC++) {
          if (monthC == 0) {
            beginBalance = yearData[monthC].beginningBalance;
          }
          totalPrincipal += Math.round((yearData[monthC].emi - yearData[monthC].interest) * 100) / 100;
          princ = yearData[monthC].endingBalance;
          totalInterest = yearData[monthC].totalInterest;
          totalAmount = yearData[monthC].totalAmount;
          yearTotal += yearData[monthC].emi;
          endingBalance = yearData[monthC].endingBalance;
          yearN = new Date(yearData[monthC].year, 0, 1),
            year = yearData[monthC].year;
          totalPrincipalYear += Math.round((yearData[monthC].emi - yearData[monthC].interest) * 100) / 100;
          totalInterestYear += yearData[monthC].interest;
        }
        this.yearWiseData.push({
          beginningBalance: beginBalance,
          totalInterest: Math.round(this.totalInterest * 100) / 100,
          totalPrincipal: totalPrincipal,
          totalAmount: Math.round(totalAmount * 100) / 100,
          yearTotal: Math.round(yearTotal * 100) / 100,
          endingBalance: Math.round(endingBalance * 100) / 100,
          yearN: yearN,
          year: year,
          yearPrincipal: totalPrincipalYear,
          yearInterest: totalInterestYear
        });
        yearTotal = 0;
        totalPrincipalYear = 0;
        totalInterestYear = 0;
      }
    }
  }


  /*
      prepare quarterly data based on dataUnits
      sum of prinicipalValue/interest/repayment etc of 3 months
  */
  public calculateQuarterlyData() {
    /* manipulate quarterly data */
    this.quarterlyData = [];
    let beginBalance = 0;
    let princ: number = this.principalValue + this.brokerageAmount;
    let totalInterest = 0;
    let totalPrincipal = 0;
    let totalAmount = 0;
    let endingBalance = 0;
    let yearTotal = 0;
    let yearN: any = null;
    let year: any = null;
    let totalPrincipalYear = 0;
    let totalInterestYear = 0;
    const quarterCount = 1;
    for (let monthC = 0; monthC <= this.dataUnits.length - 1; monthC++) {
      let prevQuarterOfTheYear: any = null;
      if (monthC != 0) {
        prevQuarterOfTheYear = this.quarterOfTheYear(new Date(this.dataUnits[monthC - 1].date));
      } else {
        prevQuarterOfTheYear = this.quarterOfTheYear(new Date(this.dataUnits[monthC].date));
      }
      const quarterOfTheYear = this.quarterOfTheYear(new Date(this.dataUnits[monthC].date));

      const nextQuarterOfTheYear = (monthC != this.dataUnits.length - 1) ? this.quarterOfTheYear(new Date(this.dataUnits[monthC + 1].date)) : quarterOfTheYear;

      if (monthC == 0 || quarterOfTheYear > prevQuarterOfTheYear) {
        beginBalance = this.dataUnits[monthC].beginningBalance;
      }
      totalPrincipal += Math.round((this.dataUnits[monthC].emi - this.dataUnits[monthC].interest) * 100) / 100;

      totalInterest = this.dataUnits[monthC].totalInterest;
      totalAmount = this.dataUnits[monthC].totalAmount;
      yearTotal += this.dataUnits[monthC].emi;
      endingBalance = this.dataUnits[monthC].endingBalance;
      yearN = new Date(this.dataUnits[monthC].year, 0, 1),
        year = this.dataUnits[monthC].year;
      totalPrincipalYear += Math.round((this.dataUnits[monthC].emi - this.dataUnits[monthC].interest) * 100) / 100;
      totalInterestYear += this.dataUnits[monthC].interest;

      if (quarterOfTheYear != nextQuarterOfTheYear || monthC == this.dataUnits.length - 1) {
        this.quarterlyData.push({
          month: this.dataUnits[monthC].month,
          beginningBalance: beginBalance,
          totalInterest: Math.round(this.totalInterest * 100) / 100,
          totalPrincipal: totalPrincipal,
          totalAmount: Math.round(totalAmount * 100) / 100,
          yearTotal: Math.round(yearTotal * 100) / 100,
          endingBalance: Math.round(endingBalance * 100) / 100,
          yearN: yearN,
          year: year,
          yearPrincipal: totalPrincipalYear,
          yearInterest: totalInterestYear,
          quarter: quarterOfTheYear
        });
        yearTotal = 0;
        totalPrincipalYear = 0;
        totalInterestYear = 0;
      }
      princ = this.dataUnits[monthC].endingBalance;
    }
  }


  calculateTableRepayment(rate: number, amountFinance: number,
                          repaymentFrequency: RepaymentFrequencyType,
                          brokerageAmount = 0,
                          residualAmount: number,
                          repaymentType: RepaymentType):{amountFinance: number, data: {repayment: number, loanTerm: number}[]}[] {
    let amountFinanced = []
    let loanTerms = [24, 36, 48, 60]
    let resultObject: any[] = []
    let repaymentFrequencyPeriod = 12;
    if (repaymentFrequency == 'Fortnightly') {
      repaymentFrequencyPeriod = 26;
    } else if (repaymentFrequency == 'Weekly') {
      repaymentFrequencyPeriod = 52;
    } else if (repaymentFrequency == 'Monthly') {
      repaymentFrequencyPeriod = 12;
    }
    let frate: any = 0;
    let nper: any;
    let roundpv = Math.ceil(amountFinance * 0.1 / 1000) * 1000
    amountFinance=Math.ceil(amountFinance/1000)*1000
    if (amountFinance <= roundpv) {
      for (let i = 0; i <= 4; i++) {
        let financeAmount = amountFinance
        amountFinanced.push(financeAmount)
        amountFinance = amountFinance + roundpv
      }
    } else {
      for (let i = 0; i <= 4; i++) {
        let amtFinance
        if (i < 2) {
          if (i === 0) {
            amtFinance = amountFinance - (roundpv + roundpv)
          }
          else {
            amtFinance = amountFinance - roundpv
          }
        } if (i === 2) {
          amtFinance = amountFinance
        } if (i > 2) {
          if (i === 3) {
            amtFinance = amountFinance + roundpv
          }
          else {
            amtFinance = amountFinance + (roundpv + roundpv)
          }
        }
        if (amtFinance !== undefined) {
          amountFinanced.push(amtFinance)
        }
      }
    }
    const fv: number = -residualAmount;
    const type: any = repaymentType == 'arrears' ? 0 : 1;
    amountFinanced.forEach(e => {
      let data: any[] = []
      loanTerms.forEach(element => {
        let obj = { repayment: 0, loanTerm: 0 }
        nper = repaymentFrequencyPeriod * (element / 12);
        frate = parseFloat((rate / (repaymentFrequencyPeriod * 100)).toString());
        let totalRepayment = this.PMT(frate, nper, e+brokerageAmount, fv, type);
        totalRepayment = -totalRepayment.toFixed(2);
        obj.loanTerm = element
        obj.repayment = totalRepayment
        data.push(obj)
      })
      resultObject.push({ amountFinance: e, data: data })
    });
    return resultObject
  }

  public getInterest(): number {
    return this.interestValue ? parseFloat('' + this.interestValue / this.repaymentFrequencyPeriod / 100) : 0;
  }

  public getCurrencyVal(value: number): number {
    return value;
  }

  isFloat(n: number) {
    return Number(n) === n && n % 1 !== 0;
  }

  /*
      Return quarter of the given date(Date javascript)
      Q1 Jan - March
      Q2 Apr - Jun
      Q3 July- Sept
      Q4 Oct - Dec
  */
  public quarterOfTheYear(date: Date) {
    const month = date.getMonth() + 1;
    return (Math.ceil(month / 3));
  }


  /*
      Calculate and return repayment based on params given
  */
  getRepayment(rate: number, loanTerms: number, principalValue: number, repaymentFrequency: RepaymentFrequencyType,
               brokerageAmount = 0, residualAmount = 0, repaymentType: RepaymentType = 'advance') {
    residualAmount = isNaN(residualAmount) == false ? residualAmount : 0;
    brokerageAmount = isNaN(brokerageAmount) == false ? brokerageAmount : 0;
    principalValue = isNaN(principalValue) == false ? principalValue : 0;
    let repaymentFrequencyPeriod = 12;
    if (repaymentFrequency == 'Fortnightly') {
      repaymentFrequencyPeriod = 26;
    } else if (repaymentFrequency == 'Weekly') {
      repaymentFrequencyPeriod = 52;
    } else if (repaymentFrequency == 'Monthly') {
      repaymentFrequencyPeriod = 12;
    }
    const tent: number = loanTerms / 12 * repaymentFrequencyPeriod;
    let frate: any = 0;
    let nper: any;
    const pv: any = principalValue + brokerageAmount;
    const fv: number = -residualAmount;
    const type: any = repaymentType == 'arrears' ? 0 : 1;
    nper = repaymentFrequencyPeriod * (loanTerms / 12);
    frate = parseFloat((rate / (repaymentFrequencyPeriod * 100)).toString());
    console.log(frate, nper, pv, fv, type);
    let totalRepayment = this.PMT(frate, nper, pv, fv, type);
        totalRepayment = -totalRepayment.toFixed(2);
    return totalRepayment;
  }


  getApplicableInterestRate(comparison: any, value: any) {
    let rate = 0;
    if (comparison && comparison.length > 0) {
      for (let i = 0; i <= comparison.length - 1; i++) {
        if (comparison[i].To === undefined && comparison[i].From === undefined) {
          rate = parseFloat(comparison[i].Rate);
        } else if (comparison[i].To === 'max') {
          rate = parseFloat(comparison[i].Rate);
        } else if (comparison[i].From === undefined && comparison[i].To !== undefined) {
          if (value <= parseFloat(comparison[i].To)) {
            rate = parseFloat(comparison[i].Rate);
          }
        } else if (comparison[i].To === undefined && comparison[i].From !== undefined) {
          if (value > parseFloat(comparison[i].From)) {
            rate = parseFloat(comparison[i].Rate);
          }
        } else {
          if (value <= parseFloat(comparison[i].To) && value > parseFloat(comparison[i].From)) {
            rate = parseFloat(comparison[i].Rate);
          }
        }
      }
    }
    return _.round(rate, 2);
  }




  calculateResidualValue(prinicipalAmount: number, balloonPaymentPercent: number) {
    const repayment = prinicipalAmount;
    let rv: any = 0;
    rv = Math.round((repayment * balloonPaymentPercent) / 100 * 100) / 100;
    return rv;
  }

  // Push an additive rate component to the calculation log only if it contributes a non-zero rate.
  // Section headers, informational entries (no rate), and the final Total row should call calculationLog.push directly.
  private pushRateLog(
    calculationLog: {log: string, rate?: number}[],
    log: string,
    rate: number | null | undefined,
  ): void {
    if (!rate) return;
    calculationLog.push({log, rate});
  }


  getTotalInterestRateForInsurancePremiumFinance(rateCard: RatecardDetails, form: {totalPremiumAmount: number}): TermRateForInsurancePremium {
    const calculationLog: {log: string, rate?: number }[] = [];
    calculationLog.push({log: `Insurance Premium interest rate calculation`, rate: undefined});

    const rateCardBaseRate= rateCard.BaseInterestRate;
    let baseRate = rateCardBaseRate;
    const loanAmount = form.totalPremiumAmount;
    this.pushRateLog(calculationLog, `BaseRate`, rateCardBaseRate);

    //rate changes based on Loan Amount
    const loanAmountRate = this.getApplicableInterestRate(rateCard.LoanAmount, loanAmount);
    baseRate += (loanAmountRate != null) ? loanAmountRate : 0;
    this.pushRateLog(calculationLog, `LoanAmount ${loanAmount} Rate `, loanAmountRate);


    //rate changes based on doc fee
    let prinicipalAmount= loanAmount;
    let docfee = this.getApplicableInterestRate(rateCard.DocFee, loanAmount);
    docfee = (docfee != null) ? parseFloat(docfee as any) : 0;
    prinicipalAmount += docfee;
    calculationLog.push({log:`Doc Fee ${docfee} added to principalAmount ${loanAmount}`});

    const totalInterest = _.round(baseRate, 2);
    calculationLog.push({log: `Total`, rate: totalInterest});

    console.table(calculationLog);

    return {
      brokerage: 0,
      basicInterest: rateCard.BaseInterestRate,
      brokerageAmount: 0,
      docFee: docfee,
      totalInterest,
      RV: 0,
      LVR: 0,
      prinicipalAmount: prinicipalAmount,
      brokerOriginationFee: 0,
      calculationLog,
    }
  }


  getTotalInterestRateForBusinessOverdraft(rateCard: RatecardDetails, form: FormDataForBusinessOverdraft): TermRateForBusinessOverdraft {
    const calculationLog: {log: string, rate?: number }[] = [];
    calculationLog.push({log: `Business Overdraft interest rate calculation`, rate: undefined});

    const brokerageAmountSf = form.brokerageAmount;
    const facilityLimit = form.facilityLimit;
    const docFee: number = form.docFee != undefined ? form.docFee : this.getApplicableInterestRate(rateCard.DocFee, form.facilityLimit);
    const rateCardFacilityFeePercentage = rateCard.FacilityFee ?? 0;
    const monthlyFacilityFee = ((rateCardFacilityFeePercentage / 100) * facilityLimit) / 12
    let brokerage = BUSINESS_OVERDRAFT_BASE_BROKERAGE_PERCENT;
    const availableAmount: any = facilityLimit - docFee;
    // ZEN-818 follow-up: 3-tier model (TIB x EF Director Score x Property Backed).
    // Rate cards always have Tiers populated post-migration; existing applications
    // that predate the new form fields fall through the UI helpers
    // (applicationToGstAgeMonths / applicationToEquifaxDirectorScoreMeetsThreshold).
    const { tier, rate: tierRate } = resolveBusinessTierRate('BusinessOverdraft', form, rateCard);
    let interestRate: any = tierRate;
    calculationLog.push({log: `Tier ${tier} rate`, rate: tierRate});
    const rbaRate = rateCard.rbaRate;
    interestRate += rbaRate;
    this.pushRateLog(calculationLog, `RbaRate`, rbaRate);

    // add credit rate adjustment if applicable
    if(form.creditRateAdjustment){
      const creditAdjustment = ((!_.isNil(form.creditRateAdjustment)) ? form.creditRateAdjustment : 0);
      interestRate += creditAdjustment;
      this.pushRateLog(calculationLog, `CreditRateAdjustment`, creditAdjustment);
    }

    // additional commission: +1% brokerage, +1.5% interest rate uplift.
    // Only the rate-side uplift is logged here so the calculation log column
    // sums to the displayed total interest rate. The brokerage-side uplift is
    // reflected in the Brokerage(ex GST) row of the breakup, not in the rate.
    if (form.additionalCommission) {
      brokerage += BUSINESS_OVERDRAFT_ADDITIONAL_COMMISSION_BROKERAGE_UPLIFT_PERCENT;
      interestRate += BUSINESS_OVERDRAFT_ADDITIONAL_COMMISSION_RATE_UPLIFT_PERCENT;
      calculationLog.push({log: `Additional commission rate uplift`, rate: BUSINESS_OVERDRAFT_ADDITIONAL_COMMISSION_RATE_UPLIFT_PERCENT});
    }

    const brokerageDollar  = (brokerage/100) * facilityLimit;
    calculationLog.push({log: `Total`, rate: interestRate});
    console.table(calculationLog);

    const termRate: TermRateForBusinessOverdraft = {
      facilitylimit: facilityLimit,
      monthlyFacilityFee: parseFloat(monthlyFacilityFee.toFixed(2)),
      docFee: docFee,
      brokerageDollar: brokerageAmountSf != undefined ? brokerageAmountSf : parseFloat(brokerageDollar.toFixed(2)),
      brokerage: parseFloat(brokerage.toFixed(2)),
      amountAvailable: availableAmount,
      interestRate: _.round(interestRate, 2),
      rbaRate: rateCard.rbaRate,
      margin: form.margin ?? _.round(interestRate, 2) - rateCard.rbaRate,
      monthlyFacilityFeePercentage: rateCardFacilityFeePercentage,
      calculationLog,
    }
    return termRate;
  }


  getTotalInterestRateForBusinessFinance(rateCard: RatecardDetails, form: FormDataForBusinessFinance, companyBureauReport: any, abnAgeAboveThresholdOverride?: boolean): TermRateForBusinessFinance {

    const calculationLog: {log: string, rate?: number }[] = [];
    calculationLog.push({log: `Business Finance interest rate calculation`});

    const loanAmount = form.loanAmount;
    const loanTerms: any = form.loanTerms; // this.businessLoanForm.get('loanTerms').value; // in months
    const brokerage = form.brokerage; // this.businessLoanForm.get('brokerage').value;
    const adversOnFile = form.adverseOnFile; // this.businessLoanForm.get('adverseOnFile').value;
    const brokerageAmountSF = form.brokerageAmount;
    const docFeeFinanced = !!form?.docFeeFinanced;
    const creditRateAdjustment = form.creditRateAdjustment;
    console.log('form=====================', form)

    // ZEN-818 follow-up: 3-tier model (TIB x EF Director Score x Property Backed).
    // Rate cards always have Tiers populated post-migration.
    const { tier, rate: tierRate } = resolveBusinessTierRate('BusinessLoans', form, rateCard);
    let baseRate: number = tierRate;
    calculationLog.push({log: `Tier ${tier} rate`, rate: tierRate});

    if (adversOnFile) {
      const rate = (rateCard.AdversOnFile != null) ? (rateCard.AdversOnFile) : 0;
      baseRate += rate;
      this.pushRateLog(calculationLog, `AdverseOnFile`, rate);
    }

    // rate changes based on loan terms
    let loanTermsRate: any = this.getApplicableInterestRate(rateCard.LoanTerms, loanTerms);
    loanTermsRate = (loanTermsRate != null) ? parseFloat(loanTermsRate) : 0;
    baseRate += loanTermsRate;
    this.pushRateLog(calculationLog, `LoanTerms ${loanTerms}`, loanTermsRate);

    // rate changes based on Brokerage
    let brokerageRate: any = this.getApplicableInterestRate(rateCard.Brokerage, brokerage);
    brokerageRate = (brokerageRate != null) ? parseFloat(brokerageRate) : 0;
    baseRate += brokerageRate;
    this.pushRateLog(calculationLog, `Brokerage ${brokerage}`, brokerageRate);

    // rate changes based on Loan Amount
    let loanAmountRate: any = this.getApplicableInterestRate(rateCard.LoanAmount, loanAmount);
    loanAmountRate = (loanAmountRate != null) ? parseFloat(loanAmountRate) : 0;
    baseRate += loanAmountRate;
    this.pushRateLog(calculationLog, `LoanAmount ${loanAmount}`, loanAmountRate);


    // rate changes based on doc fee
    let prinicipalAmount: any = loanAmount;
    // get applicable doc fee just to display in repayment popup
    const docfee: any = this.getApplicableInterestRate(rateCard.DocFee, loanAmount);
    // ZEN-818 follow-up: no separate ABNorGSTAge uplift here — GST/TIB age is already
    // priced into tier eligibility via resolveBusinessTierRate above, and ABNorGSTAge is
    // no longer exposed/maintained on the Business Loan rate card (rate-card-detail.page.ts
    // only wires it up for Asset Finance), so stacking it on top double-counts ABN/GST age.

    const brokerageAmount = brokerageAmountSF != undefined ? brokerageAmountSF : this.calculateBrokerageAmount(loanAmount, brokerage);
    // removing this as now the portal rate will be taken into consideration
    const docFee = form.docFee != undefined ? form.docFee : docfee ;
    // apply doc fee if Fee financed is Yes and add in principal amount
    if (docFeeFinanced) {
      prinicipalAmount += (docFee != null) ? Number(docFee) : 0;
      calculationLog.push({log: `DocFeeFinanced, add docFee ${docFee} to principal amount ${loanAmount}`});
    }
    // add credit rate adjustment if applicable
    if(creditRateAdjustment){
      const rate = ((creditRateAdjustment != null) ? creditRateAdjustment : 0);
      baseRate += rate;
      this.pushRateLog(calculationLog, `CreditRateAdjustment`, rate);
    }
    const basicInterest = rateCard.BaseInterestRate;
    const totalInterest = _.round(baseRate, 2);

    calculationLog.push({log: `Total`, rate: totalInterest});
    console.table(calculationLog);

    const termRate: TermRateForBusinessFinance = {
      brokerage,
      basicInterest,
      brokerageAmount,
      docFee,
      totalInterest,
      RV: 0,
      LVR: 0,
      prinicipalAmount: prinicipalAmount,
      brokerOriginationFee: 0,
      calculationLog,
    }
    return termRate;
  }

  calculateBrokerageAmount(loanAmount: number, brokerage: number) {
    const LA = loanAmount;
    let brokerageAmount =  0;
    brokerageAmount = Math.round((LA * brokerage) / 100 * 100) / 100;
    return brokerageAmount;
  }

  calculateFaciltyEstablishmentFee(facilityPercent: number, facilityFee: number) {
    const FP = facilityPercent;
    // (facilityEstablishmentFeePercent/100)*(this.formControlStep2FacilityEstablishmentFee.value)
    let establishmentFee =  0;
    establishmentFee = (facilityPercent/100)*(facilityFee);
    return establishmentFee;
  }

  getTotalInterestRateForAssetFinance(rateCard: RatecardDetails,
                                      form: RepaymentEstimationFormData,
                                      vehicles: {goodretail?: number, newprice?: number}[] = [],
                                      companyBureauReport: {GSTAgeMonths?: number, GSTAgeMonthsWithContinuity?: number, ABNAgeMonths?: number} | null = null,
                                      fixedBalloonPayment: any = null,
                                      abnAgeAboveThresholdOverride?: boolean): TermRateForAssetFinance {
    const calculationLog: {log: string, rate?: number }[] = [];
    calculationLog.push({log: `Asset Finance interest rate calculation`, rate: undefined});

    let baseRate: any = rateCard.BaseInterestRate;

    // Special base rate override for GST/ABN > 5 years and new tier 1/2 assets (excluding Yellow Goods/Material Handling)
    const startDate = new Date('2025-05-26T09:00:00+10:00');
    const endDate = new Date('2025-07-31T23:59:59+10:00');
    const today = new Date();

    const getSpecialBaseRate = () => {
      if (
        today >= startDate && today <= endDate && 
        (companyBureauReport?.GSTAgeMonths ?? 0) > 60 &&
        (companyBureauReport?.ABNAgeMonths ?? 0) > 60 &&
        form.assetCondition === 'New' && 
        form.assetCategory !== '130' // Yellow Goods/Material Handling
      ) {
        return 6.89;
      }
      return null;
    }

    
    this.pushRateLog(calculationLog, `baseRate`, baseRate);

    const assetCondition = form.assetCondition;                                      // 'Used', 'New'
    const assetCat = form.assetCategory;                                                    // config.assetCat[].cat.index (string)
    const assetType = form.assetType;                                                       // config.assetCat[].type.index (string)
    const assetYear: any = form.assetSpec.year;                                                    // number
    const ageOfAssetToday: number =
      Math.max(0, new Date().getFullYear() - parseInt(assetYear, 10)-1) ;              // number
    const loanAmount = form.loanAmount;                                                     // number
    const loanTerms: number = form.loanTerms;                                                  // in months
    const propertyOwnership = form.propertyOwnership;                                       // boolean
    const docFeeFinanced = form.docFeeFinanced;                                             // boolean
    let brokerOriginationFee = form.brokerOriginationFee;                                 // number
    let brokerage = form.brokerage;                                                       // number
    const adverseOnFile = form.adverseOnFile;                                               // boolean
    const highEquifaxScore = form.equifaxDirectorScoreMeetsProductThreshold;                                     // boolean
    const vehicle = form.assetSpec.vehicle;                                                 // string
    const balloonPayment = form.balloonPayment;                                             // number (percentage)
    const financeType = form.financeType;                                                   // 'rental' | 'operating-lease' | 'chattel-mortgage' | 'finance-lease' | 'software-agreement'
    const isAssetBacked = propertyOwnership === true;                                       // boolean
    const eotAge: number = ageOfAssetToday + (loanTerms / 12);                                       // EOT Age (number)
    const deposit = form?.deposit ?? 0;                                                           // number
    const brokerageAmountSF = form?.brokerageAmount;                                     // number
    const balloonPaymentAmount = form.balloonPaymentAmount;                                 // number
    const transactionType = form.transactionType;                                                           // TransactionType
    const isTransactionTypeInRateCard = transactionType ? (rateCard.TransactionTypeRate?.transactionTypes ?? []).includes(transactionType) : false;
    const isTruckGrossWeigh12T = form.truckGrossWeight == TruckGrossVehicleWeightSelection.Massive;
    console.log("=======form ", form);

    let abnAgeInMonths = 0;                                                                 // ABN Age (in months)
    let hasCompanyAbnAge = false;                                                           // boolean: has abn age or not
    let tier = null;                                                                        // tier category
    let additionalRate = 0;                                                                 // all additional rates

    // update brokerage
    if (brokerageAmountSF != undefined) {
      console.log('=======here ', brokerageAmountSF);
      brokerage = Math.round(brokerageAmountSF * 10000 / loanAmount) / 100;
    }

    // initialize ABN age in months: use override if provided, otherwise use actual ABN age from bureau report
    if (abnAgeAboveThresholdOverride === true) {
      // Override: treat as >= 24 months threshold (no ABN age penalty)
      hasCompanyAbnAge = true;
      abnAgeInMonths = 999;
    } else if (abnAgeAboveThresholdOverride === false) {
      // Override: treat as < 24 months threshold (apply ABN age penalty)
      hasCompanyAbnAge = true;
      abnAgeInMonths = 1;
    } else if (companyBureauReport?.ABNAgeMonths) {
      // No override: use actual ABN age from bureau report
      hasCompanyAbnAge = true;
      abnAgeInMonths = companyBureauReport.ABNAgeMonths ?? 0;
    }

    // tier classifications
    if (assetCat && !isNaN(ageOfAssetToday) && loanTerms) {
      if (assetCat && TIER1_ASSET_CATS.includes(assetCat)) {
        tier = 1;
      } else if (TIER2_ASSET_CATS.includes(assetCat)) {
        tier = 2;
      } else if (TIER3_ASSET_CATS.includes(assetCat)) {
        tier = 3;
      } else if (TIER4_ASSET_CATS.includes(assetCat)) {
        tier = 4;
      }
    }

    let assetAgeMatrix;
    switch (tier) {
      case 1: {// tier 1
        // ZEN-XXX: Single-rate lookup. 'New' uses AssetBacked.New.Rate; any
        // other condition falls into the matching age bucket in YearRanges.
        const tier1AssetFinanceRate: AssetFinanceTier1 = parseJSON(rateCard.AssetFinanceTier1);
        const tier1AssetFinanceRateRange = tier1AssetFinanceRate.AssetBacked;

        let rate: number;
        let rangeLabel: string;
        if (assetCondition === 'New') {
          rate = resolveAssetFinanceTierRates(tier1AssetFinanceRateRange).New;
          rangeLabel = 'New';
        } else {
          assetAgeMatrix =
            parseAssetFinanceTier(ageOfAssetToday, tier1AssetFinanceRateRange.YearRanges) as AssetFinanceTier1Range['YearRanges'][number];
          if (!assetAgeMatrix) {
            throw Error(`Tier 1: can't find Asset Age Matrix for ${ageOfAssetToday}`);
          }
          rate = resolveAssetFinanceTierRates(tier1AssetFinanceRateRange)[assetAgeMatrix.YearRangeType];
          rangeLabel = assetAgeMatrix.YearRange;
        }

        const specialRate = getSpecialBaseRate();
        if (specialRate && specialRate < rate) {
          baseRate += specialRate;
          calculationLog.push({log: 'Headline rate override', rate: specialRate});
        } else {
          baseRate += rate;
          calculationLog.push({log: `Tier1 Asset: range=${rangeLabel}, assetAgeYear=${ageOfAssetToday}, abnAgeMonth=${abnAgeInMonths}`, rate});
        }
        break;
      }
      case 2: {// tier 2
        const tier2AssetFinanceRate: AssetFinanceTier2 = parseJSON(rateCard.AssetFinanceTier2);
        const tier2AssetFinanceRateRange = tier2AssetFinanceRate?.AssetBacked as AssetFinanceTier2Range;

        let rate: number;
        let rangeLabel: string;
        if (assetCondition === 'New') {
          rate = resolveAssetFinanceTierRates(tier2AssetFinanceRateRange).New;
          rangeLabel = 'New';
        } else {
          assetAgeMatrix =
            parseAssetFinanceTier(ageOfAssetToday, tier2AssetFinanceRateRange.YearRanges) as AssetFinanceTier2Range['YearRanges'][number];
          if (!assetAgeMatrix) {
            throw Error(`Tier 2: can't find Asset Age Matrix for ${ageOfAssetToday}`);
          }
          rate = resolveAssetFinanceTierRates(tier2AssetFinanceRateRange)[assetAgeMatrix.YearRangeType];
          rangeLabel = assetAgeMatrix.YearRange;
        }

        const specialRate = getSpecialBaseRate();
        if (specialRate && specialRate < rate) {
          baseRate += specialRate;
          calculationLog.push({log: 'Headline rate override', rate: specialRate});
        } else {
          baseRate += rate;
          calculationLog.push({log: `Tier2 Asset: range=${rangeLabel}, assetAgeYear=${ageOfAssetToday}, abnAgeMonth=${abnAgeInMonths}`, rate});
        }
        break;
      }
      case 3: { // tier 3
        // 5.7.0: 4-bucket lookup mirroring Tier 1/2. 'New' uses AssetBacked.New.Rate;
        // any other condition falls into the matching age bucket in YearRanges.
        // Solar (106) now prices off the same matrix as every other Tier 3 asset.
        const tier3AssetFinanceRate: AssetFinanceTier3 = parseJSON(rateCard.AssetFinanceTier3);
        const tier3AssetFinanceRateRange = tier3AssetFinanceRate?.AssetBacked as AssetFinanceTier3Range;

        let rate: number;
        let rangeLabel: string;
        if (assetCondition === 'New') {
          rate = resolveAssetFinanceTierRates(tier3AssetFinanceRateRange).New;
          rangeLabel = 'New';
        } else {
          assetAgeMatrix =
            parseAssetFinanceTier(ageOfAssetToday, tier3AssetFinanceRateRange.YearRanges) as AssetFinanceTier3Range['YearRanges'][number];
          if (!assetAgeMatrix) {
            throw Error(`Tier 3: can't find Asset Age Matrix for ${ageOfAssetToday}`);
          }
          rate = resolveAssetFinanceTierRates(tier3AssetFinanceRateRange)[assetAgeMatrix.YearRangeType];
          rangeLabel = assetAgeMatrix.YearRange;
        }

        baseRate += rate;
        this.pushRateLog(calculationLog, `Tier3 Asset: range=${rangeLabel}, assetAgeYear=${ageOfAssetToday}`, rate);
        break;
      }
      case 4: { // tier 4
        const tier4AssetFinanceRate: AssetFinanceTier4 = parseJSON(rateCard.AssetFinanceTier4);
        const tier4MonthsCard = tier4AssetFinanceRate.AssetBacked;
        const u = tier4MonthsCard.find(m => m.Type === assetCondition);
        if (!u) {
          console.error(`Tier 4: can't find Used Matrix`);
        } else {
          const [newRate, usedRate] = resolveRelativeRates([
            tier4MonthsCard.find(m => m.Type === 'New')?.Rate,
            tier4MonthsCard.find(m => m.Type === 'Used')?.Rate,
          ]);
          const rate = assetCondition === 'New' ? newRate : usedRate;
          baseRate += rate
          const log = (`Tier4 Asset: rate: ${rate}, assetCondition: ${assetCondition}, eotAgeYear: ${eotAge}`);
          this.pushRateLog(calculationLog, log, rate);
        }
        break;
      }
    }

    // adverse on file
    if (adverseOnFile) {
      const adverseOnFileRate = (rateCard.AdversOnFile != null) ? parseFloat(String(rateCard.AdversOnFile)) : 0;
      additionalRate += adverseOnFileRate;
      this.pushRateLog(calculationLog, `Adverse on File (additional rate)`, adverseOnFileRate);
    }

    // low director-score uplift
    if (!highEquifaxScore) {
      const lowDirectorScoreUpliftRate = (rateCard.LowDirectorScoreUplift != null) ? parseFloat(String(rateCard.LowDirectorScoreUplift)) : 0;
      additionalRate += lowDirectorScoreUpliftRate;
      calculationLog.push({log: `LowDirectorScoreUplift (additional rate)`, rate: lowDirectorScoreUpliftRate});
    }

    // ABN age
    if (hasCompanyAbnAge) {
      const abnRate = this.getApplicableInterestRate(rateCard.ABNorGSTAge, abnAgeInMonths);
      additionalRate += (abnRate != null) ? parseFloat(abnRate as any) : 0;
      const overrideIndicator = abnAgeAboveThresholdOverride !== undefined ? ' (override)' : '';
      this.pushRateLog(calculationLog, `ABN Age (${abnAgeInMonths} Months)${overrideIndicator} (additional rate)`, abnRate);
    }

    // rate changes based on Loan Amount
    let loanAmountRate: any = this.getApplicableInterestRate(rateCard.LoanAmount, loanAmount);
    loanAmountRate = (loanAmountRate != null) ? parseFloat(loanAmountRate) : 0;
    additionalRate += loanAmountRate;
    this.pushRateLog(calculationLog, `LoanAmountRate (additional rate)`, loanAmountRate);

    // loan terms
    let loanTermsRate = this.getApplicableInterestRate(rateCard.LoanTerms, loanTerms);
    loanTermsRate = (loanTermsRate != null) ? parseFloat(loanTermsRate as any) : 0;
    additionalRate += loanTermsRate;
    this.pushRateLog(calculationLog, `LoanTerms ${loanTerms} (additional rate)`, loanTermsRate);

    // asset category
    if (rateCard.AssetCategory.length > 0) {
      rateCard.AssetCategory.forEach((item: {Cat: string, Rate: number}) => {
        if (item.Cat === assetCat) {
          const assetCategoryRate = (item.Rate != null) ? parseFloat(String(item.Rate)) : 0;
          additionalRate += assetCategoryRate;
          this.pushRateLog(calculationLog, `Asset Category ${assetCat} (additional rate)`, assetCategoryRate);
        }
      });
    }

    // asset type
    if (rateCard.AssetType.length > 0) {
      rateCard.AssetType.forEach((item: {Cat: string, Type: string, Rate: number}) => {
        if (item.Cat === assetCat && item.Rate != null) {
          const type = item.Type;
          if ((type) === assetType) {
            const assetTypeRate = item.Rate !== null ? item.Rate : 0;
            additionalRate += assetTypeRate;
            this.pushRateLog(calculationLog, `Asset Type ${assetType} (additional rate)`, assetTypeRate);
          }
        }
      });
    }


    // Deposit uplift
    if (tier === 1 && !propertyOwnership && deposit < loanAmount * 0.2) {
      const lowDepositUplift = rateCard.LowDepositUplift ?? 1;
      additionalRate += lowDepositUplift;
      this.pushRateLog(calculationLog, `Low Deposit Uplift (additional rate)`, lowDepositUplift);
    }


    // no asset backed lift
    if (!isAssetBacked) {
      const noAssetBackedRate = rateCard.NoAssetBacked ?? 0;
      additionalRate += noAssetBackedRate;
      this.pushRateLog(calculationLog, `NoAssetBacked (additional rate)`, noAssetBackedRate);
    }

    // 12T truck uplift
    if (isAssetTypeAnyTruck(assetCat, assetType) && isTruckGrossWeigh12T) {
      const truck12tRate = rateCard.Truck12T ?? 0;
      additionalRate += truck12tRate;
      this.pushRateLog(calculationLog, `12T truck uplift (additional rate)`, truck12tRate);
    }

    // transaction type rate
    if (transactionType && rateCard.TransactionTypeRate) {
      if(isTransactionTypeInRateCard) {
        // transaction type is listed in rate card transaction type, apply rate
        const rate = rateCard.TransactionTypeRate.rate;
        additionalRate += rate;
        this.pushRateLog(calculationLog, `TransactionType ${transactionType} Uplift`, rate);
      }
    }

    // cap additional rate
    const capRate = rateCard.CapRate ?? 0;
    if (capRate > 0 && additionalRate > capRate) {
      additionalRate = capRate;
      calculationLog.push({log: `${additionalRate} (additional rate) > ${capRate} (cap rate), capping rate to cap rate`, rate: capRate});
    }

    // brokerage
    let brokerageRate = this.getApplicableInterestRate(rateCard.Brokerage, brokerage);
    brokerageRate = (brokerageRate != null) ? parseFloat(brokerageRate as any) : 0;
    additionalRate += brokerageRate;
    this.pushRateLog(calculationLog, `Brokerage`, brokerageRate);

    // Stepped brokerage uplift:
    //   brokerage in (TIER1, TIER2] → +STEP
    //   brokerage in (TIER2, MAX]   → +2 * STEP
    if (brokerage > ASSET_FINANCE_BROKERAGE_UPLIFT_TIER2_PERCENT) {
      const brokerageUplift = ASSET_FINANCE_BROKERAGE_UPLIFT_STEP_PERCENT * 2;
      additionalRate += brokerageUplift;
      calculationLog.push({log: `Brokerage uplift (brokerage > ${ASSET_FINANCE_BROKERAGE_UPLIFT_TIER2_PERCENT}%)`, rate: brokerageUplift});
    } else if (brokerage > ASSET_FINANCE_BROKERAGE_UPLIFT_TIER1_PERCENT) {
      const brokerageUplift = ASSET_FINANCE_BROKERAGE_UPLIFT_STEP_PERCENT;
      additionalRate += brokerageUplift;
      calculationLog.push({log: `Brokerage uplift (brokerage > ${ASSET_FINANCE_BROKERAGE_UPLIFT_TIER1_PERCENT}%)`, rate: brokerageUplift});
    }

    // update base rate
    baseRate += additionalRate;
    this.pushRateLog(calculationLog, `total additionalRate`, additionalRate);

    // AZ-1651: subtract after the cap. Folding this into additionalRate lets CapRate swallow it.
    // GST age is the entity's own GSTAgeMonths. GSTAgeMonthsWithContinuity does not qualify.
    // appliedPremiumDiscount is the percent taken off this quote (0 when it does not qualify).
    // Callers persist it on PricingDetails.PremiumDiscount.
    let appliedPremiumDiscount = 0;
    const premiumDiscount = premiumDiscountPercent(rateCard.PremiumDiscount);
    const gstAgeMonths = companyBureauReport?.GSTAgeMonths ?? 0;
    if (
      premiumDiscount > 0 &&
      gstAgeMonths >= 60 &&
      form.equifaxDirectorScoreAbove700 === true &&
      propertyOwnership === true
    ) {
      appliedPremiumDiscount = premiumDiscount;
      baseRate -= premiumDiscount;
      calculationLog.push({log: `Premium Discount`, rate: -premiumDiscount});
    }

    // rate changes based on doc fee
    let prinicipalAmount: any = loanAmount;
    let docfee: any = 0;
    // get applicable doc fee just to display in repayment popup
    docfee = form.docFee == undefined ? this.getApplicableInterestRate(rateCard.DocFee, loanAmount) : form.docFee;
    // apply doc fee if Fee financed is Yes and add in principal amount
    if (docFeeFinanced) {
      prinicipalAmount += (docfee != null) ? Number(docfee) : 0;
    }

    if (isTransactionTypeInRateCard && form.docFee == undefined) {
      const additionalDocFee = rateCard.TransactionTypeRate?.additionalDocFee ?? 0;
      const oldDocFee = docfee;
      docfee = docfee + additionalDocFee;
      const msg = (`additional doc fee applied, old doc fee ${oldDocFee}, additional doc fee ${additionalDocFee}, new doc fee ${docfee}`);
      calculationLog.push({log: msg, rate: 0});
      if (docFeeFinanced) {
        const oldPrincipalAmount = prinicipalAmount;
        prinicipalAmount += additionalDocFee;
        const msg = (`additional doc fee applied to principal amount, old principal amount ${oldPrincipalAmount}, additional doc fee ${additionalDocFee}, new principal amount ${prinicipalAmount}`);
        calculationLog.push({log: msg, rate: 0});
      }
    }
    docfee = parseFloat(docfee).toFixed(2)


    // add broker origination fee
    if (brokerOriginationFee > 0) {
      prinicipalAmount += parseFloat(String(brokerOriginationFee));
    }

    // LVR
    let LVR: any = 0;
    if (this.isAssetVehicles(assetCat) && (this.isAssetNewCars(assetCat, assetType) || this.isAssetTypeLightOrHeavyTruck(assetCat, assetType))){
       let assetPrice: any = form.assetSpec.goodretail;
       if (assetPrice == null || assetPrice == '') {
         assetPrice = form.assetSpec.newprice;
       }
       if (vehicle == 'actual_model_not_yet_determined') {
         let totalGoodPrice = 0;
         for (let i = 0; i <= vehicles.length - 1; i++) {
           totalGoodPrice += ((vehicles[i].goodretail) ? vehicles[i].goodretail : vehicles[i].newprice) ?? 0;
         }
         const avgPrice = Math.round((totalGoodPrice / vehicles.length) * 100) / 100;
         LVR = Math.round(((loanAmount / avgPrice) * 100) * 100) / 100;
       } else {
         if (assetPrice != null && assetPrice != '') {
           LVR = Math.round(((loanAmount / assetPrice) * 100) * 100) / 100;
         }
       }
    }

    // add credit rate adjustment if applicable
    if(form.creditRateAdjustment){
      const creditRateAdjustment = ((!_.isNil(form.creditRateAdjustment)) ? form.creditRateAdjustment : 0);
      baseRate += creditRateAdjustment;
      this.pushRateLog(calculationLog, `Credit Rate Adjustment`, creditRateAdjustment);
    }

    const totalInterest = _.round(baseRate, 2);
    console.log('total interest', totalInterest);
    calculationLog.push({log: `Total`, rate: totalInterest});

    const brokerageAmount = brokerageAmountSF ? brokerageAmountSF : this.calculateBrokerageAmount(loanAmount, brokerage);

    let RV = fixedBalloonPayment;
    if (fixedBalloonPayment == null) {
      let ballooninvoiceAmount = form.invoiceAmount ?? 0
      RV = balloonPaymentAmount != undefined ? balloonPaymentAmount : this.calculateResidualValue(ballooninvoiceAmount, balloonPayment); // clculated based on interest.prinicipalAmount
    } else {
      RV = fixedBalloonPayment;
    }

    const termRate: TermRateForAssetFinance = {
      brokerage: brokerage,
      brokerageAmount: brokerageAmount,
      basicInterest: rateCard.BaseInterestRate,
      totalInterest: totalInterest,
      prinicipalAmount: prinicipalAmount,
      docFee: docfee,
      LVR: LVR,
      brokerOriginationFee: brokerOriginationFee,
      RV: RV,
      premiumDiscount: appliedPremiumDiscount,
      calculationLog,
    };

    console.log('getTotalInterestRate result:', termRate);
    console.table(calculationLog);

    return termRate;
  }

  getDonutChartData(): { pricipalAmt: number, interestAmt: number } {
    return {
      pricipalAmt: this.principalAmt,
      interestAmt: this.interestAmt,
    }
  }

  getBarChartQuaterlyData():MonthWiseData[] {
    this.calculateQuarterlyData();
    return this.quarterlyData;
  }

  getBarChartYearlyData(): YearWiseData[] {
    return this.yearWiseData;
  }

  getRepaymentEstimationData2(terms: TermRate,
                              repaymentFrequency: RepaymentFrequencyType,
                              repaymentType: RepaymentType): {amountFinance: number, data: {repayment: number, loanTerm: number}[]}[] {
    const basicInterest = terms.basicInterest;
    const totalInterest = terms.totalInterest;
    const principalAmount = terms.prinicipalAmount;
    const brokerageAmount = terms.brokerageAmount;
    const RV = terms.RV;
    const LVR = terms.LVR;
    const docFee = terms.docFee;
    const brokerOriginationFee = terms.brokerOriginationFee;

    return this.calculateTableRepayment(
      totalInterest,
      principalAmount,
      repaymentFrequency,
      brokerageAmount,
      RV,
      repaymentType
    )
  }

  getAmortisationData(): YearWiseDataWithChild[] {
    const yearWiseData: YearWiseDataWithChild[] = this.yearWiseData.map(r => ({ ...r, childRows: []}));
    const dataUnitsGroupByYears: any = {};
    console.log('****** dataUnits', this.dataUnits);
    for (let i = 0; i <= this.dataUnits.length - 1; i++) {
      if (dataUnitsGroupByYears[this.dataUnits[i].year] != undefined) {
        dataUnitsGroupByYears[this.dataUnits[i].year].push(this.dataUnits[i]);
      } else {
        dataUnitsGroupByYears[this.dataUnits[i].year] = [];
        dataUnitsGroupByYears[this.dataUnits[i].year].push(this.dataUnits[i]);
      }

    }
    for (let cr = 0; cr <= yearWiseData.length - 1; cr++) {
      yearWiseData[cr].childRows = dataUnitsGroupByYears[yearWiseData[cr].year];
    }
    return yearWiseData;
  }
}


export interface FormDataForBusinessFinance {
  loanAmount: number,
  loanTerms: number,
  propertyOwner: boolean,
  // docFeeFinanced: boolean,
  brokerage: number,
  adverseOnFile: boolean,
  docFeeFinanced?: boolean,
  brokerageAmount?: number,
  creditRateAdjustment?: number,
  docFee?: number,
  // ZEN-818: tier-model inputs for BusinessLoans.
  // equifaxDirectorScoreAbove700: UI flag mapped from the toggle "Equifax Director Score above 700".
  // gstAgeMonths: CompanyDetails.GSTAgeMonths — used for Tier 1 (premium) which excludes continuity.
  // gstAgeMonthsWithContinuity: CompanyDetails.GSTAgeMonthsWithContinuity — used for Tier 2/3;
  //   falls back to gstAgeMonths when omitted.
  equifaxDirectorScoreAbove700?: boolean,
  gstAgeMonths?: number,
  gstAgeMonthsWithContinuity?: number,
}

export interface FormDataForBusinessOverdraft {
  facilityLimit: number,
  loanTerms: number,
  propertyOwner: boolean,
  adverseOnFile: boolean,
  brokerageAmount?: number,
  docFee?: number,
  margin?: number,
  creditRateAdjustment?: number,
  additionalCommission?: boolean,
  // ZEN-818: tier-model inputs for BusinessOverdraft.
  // gstAgeMonths: used for Tier 1 (premium, no continuity); gstAgeMonthsWithContinuity for Tier 2/3.
  equifaxDirectorScoreAbove700?: boolean,
  gstAgeMonths?: number,
  gstAgeMonthsWithContinuity?: number,
}

export interface InsurancePremiumCalculatorData {
  monthlyInstallment: number;
  applicationFee: number;
  brokerage: number;
}

export interface RepaymentEstimationFormData {
  assetCondition: 'Used' | 'New',
  assetCategory: string,
  assetType: string,
  assetSpec: {
    year: number,
    vehicle: string,
    goodretail?: number,
    newprice?: number,
  },
  truckGrossWeight?: TruckGrossVehicleWeight,
  ageOfAssetToday: number,
  loanAmount: number,
  loanTerms: number,
  propertyOwnership: boolean,
  docFeeFinanced: boolean,
  brokerOriginationFee: number,
  brokerage: number,
  adverseOnFile: boolean,
  equifaxDirectorScoreMeetsProductThreshold: boolean,
  equifaxDirectorScoreAbove700?: boolean,
  privateSaleOrLeaseback: boolean,
  balloonPayment: number,
  financeType: FinanceType | null,
  invoiceAmount?: number,
  deposit?: number,
  brokerageAmount?: number,
  balloonPaymentAmount?: number,
  transactionType?: TransactionType,
  creditRateAdjustment?: number
  docFee?: number,
}

export interface YearWiseData {
  beginningBalance: number,
  endingBalance: number,
  totalAmount: number,
  totalInterest: number,
  totalPrincipal: number,
  year: number,
  yearN: Date;
  yearInterest: number,
  yearPrincipal: number,
  yearTotal: number,
}

export interface YearWiseDataWithChild extends YearWiseData {
  childRows: DataUnitType[];
}

export interface MonthWiseData {
  beginningBalance: number,
  endingBalance: number,
  month: string,
  quarter: number,
  totalAmount: number,
  totalInterest: number,
  totalPrincipal: number,
  year: number,
  yearInterest: number,
  yearN: Date,
  yearPrincipal: number,
  yearTotal: number,
}

export interface DataUnitType {
  month: MonthType,
  index: number,
  totalInterest: number,
  totalAmount: number,
  emi: number,
  year: number,
  beginningBalance: number,
  interest: number,
  pricipalPaid: number,
  endingBalance:  number,
  date: string,
}

export interface TermRate {
  brokerage: number,
  brokerageAmount: number,
  basicInterest: number,
  totalInterest: number,
  prinicipalAmount: number,
  docFee: number,
  LVR: number,
  brokerOriginationFee: number,
  RV: number,
  // AZ-1651: percent taken off this Asset Finance quote. 0 when the discount did not apply.
  premiumDiscount?: number,
}

export interface TermRateForAssetFinance {
  brokerage: number,
  brokerageAmount: number,
  basicInterest: number,
  totalInterest: number,
  prinicipalAmount: number,
  docFee: number,
  LVR: number,
  brokerOriginationFee: number,
  RV: number,
  // AZ-1651: percent taken off this quote. 0 when GST age, the 700 toggle, or property ownership did not qualify.
  premiumDiscount: number,
  calculationLog: {log: string, rate?: number}[],
}

export interface TermRateForInsurancePremium {
  brokerage: number,
  brokerageAmount: number,
  basicInterest: number,
  totalInterest: number,
  prinicipalAmount: number,
  docFee: number,
  LVR: number,
  brokerOriginationFee: number,
  RV: number,
  calculationLog: {log: string, rate?: number}[],
}

export interface TermRateForBusinessFinance {
  brokerage: number,
  brokerageAmount: number,
  basicInterest: number,
  totalInterest: number,
  prinicipalAmount: number,
  docFee: number,
  LVR: number,
  brokerOriginationFee: number,
  RV: number,
  calculationLog: {log: string, rate?: number}[],
}

export interface TermRateForBusinessOverdraft {
  facilitylimit:number
  monthlyFacilityFee:number
  docFee: number
  brokerage:number
  brokerageDollar: number
  amountAvailable:number
  interestRate: number,
  rbaRate: number,
  margin: number,
  monthlyFacilityFeePercentage: number,
  calculationLog: {log: string, rate?: number}[],
  anticipatedMonthlyInterest?: number,
  anticipatedMonthlyCost?: number,
  earlyTerminationFee?: number,
}


// ============= app-calculator related functions
export const calculateAssetFinanceEstimation = (
    date: Moment, data: AppCalculatorAssetFinanceData): CalculateAssetFinanceEstmationResult => {
    const paymentFrequencyType: PaymentFrequencyType | null = data.paymentFrequencyType;
    const assetConditionType: AssetConditionType | null = data.assetConditionType;
    const assetSelectionValue = data.assetSelectionValue;
    const assetYear = parseFloat(assetSelectionValue?.year ?? "0");
    const ageOfAssetToday: any = new Date().getFullYear() - assetYear;
    const loanTerms: number  = data.loanTerm;
    const balloonPayment: number  = data.balloonPayment;
    const financeType: FinanceType | null = data.financeType;

    const brokerageType: number = data.brokerage;
    const calculator = new AppCalculator();
    const loanAmount = numeral(data.loanAmount).value() ?? 0;
    const businessSearchValue = data.businessSearchValue;
    const formData: RepaymentEstimationFormData = {
        assetCondition: assetConditionType ?? 'Used',
        assetCategory: assetSelectionValue?.category.index ?? '',
        assetType: assetSelectionValue?.type.index ?? '',
        assetSpec: {
            year: assetYear,
            vehicle: assetSelectionValue?.model ?? '',
            goodretail: (assetSelectionValue?.vehicle?.goodretail) ?? 0,
            newprice: (assetSelectionValue?.vehicle?.newprice) ?? 0,
        },
        truckGrossWeight: assetSelectionValue?.truckGrossVehicleWeight ?? undefined,
        ageOfAssetToday: ageOfAssetToday,
        loanAmount: loanAmount,
        loanTerms: loanTerms,
        propertyOwnership: data.propertyOwnership ?? false,
        docFeeFinanced: data.docFeeFinanced ?? false,
        docFee: data.docFee,
        brokerOriginationFee: data.brokerOriginationFee,
        brokerage: Number(brokerageType ?? 0),
        adverseOnFile: data.adverseOnFile ?? false,
        equifaxDirectorScoreMeetsProductThreshold: data.equifaxDirectorScoreMeetsProductThreshold ?? false,
        equifaxDirectorScoreAbove700: data.equifaxDirectorScoreAbove700 ?? false,
        privateSaleOrLeaseback: data.privateSaleOrLeaseback ?? false,
        balloonPayment: balloonPayment,
        financeType: financeType ?? null,
        deposit: data?.deposit ?? 0,
        brokerageAmount: data?.brokerageAmount,
        balloonPaymentAmount: data.balloonPaymentAmount,
        transactionType: data.transactionType,
        creditRateAdjustment: data.creditRateAdjustment,
        invoiceAmount: numeral(data.invoiceAmount).value() ?? 0
    }
    // NOTE: use result only when it is an ASIC/ABN search
    const bureauReport = (businessSearchValue?.type === 'search-result' && businessSearchValue?.result) ? businessSearchValue.result : data.existingApplicationBureauReport;
    console.log('===bureauReport: ', bureauReport, businessSearchValue, data.existingApplicationBureauReport);
    let terms: any;
    // Check if manual interest rate is provided
    if (data.manualInterestRate != null && data.manualInterestRate > 0) {
      // Manual interest rate mode - use the provided interest rate instead of rate card calculation
      console.log('Using manual interest rate:', data.manualInterestRate);
        
      // Calculate other components normally (doc fee, brokerage, etc.)
      const brokerageAmount = calculator.calculateBrokerageAmount(loanAmount, brokerageType);
      let principalAmount = loanAmount;
      
      // Get doc fee
      const docfee = formData.docFee ?? calculator.getApplicableInterestRate(data.rateCard?.DocFee ?? [], loanAmount);
      
      // Apply doc fee if financed
      if (data.docFeeFinanced && docfee) {
          principalAmount += Number(docfee);
      }
      
      // Add broker origination fee
      if (formData.brokerOriginationFee > 0) {
          principalAmount += formData.brokerOriginationFee;
      }
      
      // Calculate RV (Residual Value / Balloon Payment)
      const invoiceAmount = formData.invoiceAmount ?? loanAmount;
      const RV = formData.balloonPaymentAmount ?? calculator.calculateResidualValue(invoiceAmount, balloonPayment);
      
      // Calculate LVR if applicable
      let LVR = 0;
      if (calculator.isAssetVehicles(formData.assetCategory) && 
          (calculator.isAssetNewCars(formData.assetCategory, formData.assetType) || 
           calculator.isAssetTypeLightOrHeavyTruck(formData.assetCategory, formData.assetType))) {
          const assetPrice = formData.assetSpec?.goodretail ?? formData.assetSpec?.newprice ?? 0;
          if (assetPrice > 0) {
              LVR = Math.round(((loanAmount / assetPrice) * 100) * 100) / 100;
          }
      }
      
      terms = {
          prinicipalAmount: principalAmount,
          totalInterest: data.manualInterestRate,
          brokerageAmount: brokerageAmount,
          docFee: docfee,
          RV: RV,
          LVR: LVR,
          brokerOriginationFee: formData.brokerOriginationFee,
          premiumDiscount: 0,
          calculationLog: [
              { log: 'Manual interest rate mode', rate: data.manualInterestRate },
              { log: `Interest rate (manual)`, rate: data.manualInterestRate },
          ]
      };
    } else {
      terms = calculator.getTotalInterestRateForAssetFinance(
        data.rateCard!,
        formData,
        assetSelectionValue?.vehicles ?? [],
        bureauReport,
        null, // fixedBalloonPayment
        data.abnAgeAboveThresholdOverride
      );
    }
    console.log("termsterms",terms);
    calculator.setRepaymentType(data.paymentType ? data.paymentType.toLowerCase() : "advance");
    calculator.setDateValue(date.format('DD/MM/YYYY'));
    calculator.setRepaymentFrequency(paymentFrequencyType ?? 'Monthly');
    calculator.setPrinicipalValue(terms.prinicipalAmount);
    calculator.setInterestValue(terms.totalInterest);
    calculator.setloanValue(loanTerms);
    calculator.setResidualAmount(terms.RV);
    calculator.setBrokerageAmount(terms.brokerageAmount);
    calculator.refreshUI();

    const repayment = calculator.emiAmt;
    const rst1 = calculator.getDonutChartData();
    const rst2 = calculator.getBarChartYearlyData();
    const rst3 = calculator.getBarChartQuaterlyData();
    const rst4 = calculator.getRepaymentEstimationData2(
        terms,
        paymentFrequencyType ?? 'Monthly',
        'advance',
    );
    const rst5 = calculator.getAmortisationData();
    const x: TotalPaymentBreakupDialogData = {
        repaymentEstimationData: rst4.map((rst) => ({
            amountFinance: String(rst.amountFinance),
            month24: String(rst.data.find((d) => d.loanTerm == 24)?.repayment ?? 0),
            month36: String(rst.data.find((d) => d.loanTerm == 36)?.repayment ?? 0),
            month48: String(rst.data.find((d) => d.loanTerm == 48)?.repayment ?? 0),
            month60: String(rst.data.find((d) => d.loanTerm == 60)?.repayment ?? 0),
        })),
        amortizationScheduleData: rst5.map(rst => ({
            year: String(rst.year), payment: String(rst.yearTotal), interest: String(rst.yearInterest), principal: String(rst.yearPrincipal), balance: String(rst.endingBalance),
            details: rst.childRows.map(d => ({
                monthYear: `${d.month} ${d.year}`, payment: String(d.emi), interest: String(d.interest),
                principal: String(d.pricipalPaid), balance: String(d.endingBalance)
            }))
        })),
        paymentChartData: {
            amountFinanced: rst1.pricipalAmt,
            totalInterest: terms.totalInterest,
            emiAmt: calculator.emiAmt,
            paymentFrequency: calculator.repaymentFrequency,
            principalAmt: calculator.principalAmt,
            interestAmt: calculator.interestAmt,
            totalAmt: calculator.totalAmt,
            loanTerm: calculator.loanValue, // loanValue from calculator
            lvr: terms.LVR,
            rv: terms.RV,
            brokerageAmount: calculator.brokerageAmount,
            docFee: terms.docFee,
            brokerOriginationFee: terms.brokerOriginationFee,
            applicationType: 'AssetFinance',
            invoiceAmount: data.invoiceAmount,
            deposit: data.deposit
        },
        amortizationChartData: {
            estimatedDrawdownDate: moment(calculator.dateValue),
            annualData: rst2.map(rst => ({
                year: String(rst.year), interest: rst.yearInterest, principal: rst.yearPrincipal, balance: rst.endingBalance
            })),
            quarterlyData: rst3.map(rst => ({
                quarter: `Q${rst.quarter} ${rst.year}`, balance: rst.endingBalance, principal: rst.yearPrincipal, interest: rst.yearInterest
            }))
        },
        calculationLog: terms.calculationLog,
    };

    console.log('*************** getRepaymentEstimationData rateCard', data.rateCard);
    console.log('*************** getRepaymentEstimationData formData', formData);
    console.log('*************** getRepaymentEstimationData vehicles', assetSelectionValue?.vehicles);
    console.log('*************** getRepaymentEstimationData DonutChartData', rst1);
    console.log('*************** getRepaymentEstimationData BarChartYearlyData', rst2);
    console.log('*************** getRepaymentEstimationData BarChartQuaterlyData', rst3);
    console.log('*************** getRepaymentEstimationData RepaymentEstimationData', rst4);
    console.log('*************** getRepaymentEstimationData AmortisationData', rst5);

    return {
        repayment,
        calculator,
        terms,
        totalPaymentBreakupDialogData: x,
    };
}

export const calculateSolarAssetFinanceEstimation = (
    date: Moment, data: {
        paymentFrequencyValue: PaymentFrequencyValue,
        assetConditionValue: AssetConditionValue,
        assetSelectionValue: AssetCategoryTypeSelectionValue,
        assetCategory: { index: '106', value: '' },
        assetYear: number,
        loanTermValue: LoanTermValue,
        financeType: FinanceTypeValue,
        balloonPaymentType: BalloonPaymentValue,
        brokerageValue: BrokerageSelectionValue,
        loanAmount: number,
        businessSearchValue: BusinessSearchValue,
        existingApplicationBureauReport: SearchCompanyByABNResult | null,
        propertyOwnership: boolean,
        docFeeFinanced: boolean,
        brokerOriginationFee: number,
        brokerage: number,
        adverseOnFile: boolean,
        equifaxDirectorScoreMeetsProductThreshold: boolean,
        privateSaleOrLeaseback: boolean,
        balloonPayment: number,
        rateCard: RatecardDetails | null,
    }): {
    terms: TermRateForAssetFinance,
    calculator: AppCalculator,
    repayment: number,
    totalPaymentBreakupDialogData: TotalPaymentBreakupDialogData
} => {
    const paymentFrequencyValue: PaymentFrequencyValue = data.paymentFrequencyValue;
    const assetConditionValue: AssetConditionValue = data.assetConditionValue;
    const assetSelectionValue: AssetCategoryTypeSelectionValue = data.assetSelectionValue;
    const assetYear = parseFloat("0");
    const ageOfAssetToday: any = new Date().getFullYear() - assetYear;
    const loanTermValue: LoanTermValue = data.loanTermValue;
    const financeType: FinanceTypeValue = data.financeType;
    const balloonPaymentType: BalloonPaymentValue = data.balloonPaymentType;
    const brokerageValue: BrokerageSelectionValue = data.brokerageValue;
    const calculator = new AppCalculator();
    const loanAmount = numeral(data.loanAmount).value() ?? 0;
    const businessSearchValue: BusinessSearchValue = data.businessSearchValue;
    const formData: any = {
        assetCondition: assetConditionValue?.type ?? 'Used',
        assetCategory: data.assetCategory.index ?? '',
        assetType: assetSelectionValue?.index ?? '',
        assetSpec: {
            year: assetYear
        },
        ageOfAssetToday: ageOfAssetToday,
        loanAmount: loanAmount,
        loanTerms: parseFloat(loanTermValue?.type ?? '0'),
        propertyOwnership: data.propertyOwnership,
        docFeeFinanced: data.docFeeFinanced,
        brokerOriginationFee: data.brokerOriginationFee,
        brokerage: Number(brokerageValue?.type ?? 0),
        adverseOnFile: data.adverseOnFile,
        equifaxDirectorScoreMeetsProductThreshold: data.equifaxDirectorScoreMeetsProductThreshold,
        privateSaleOrLeaseback: data.privateSaleOrLeaseback,
        balloonPayment: parseFloat(balloonPaymentType?.type ?? '0'),
        financeType: financeType?.name ?? '',
    }
    // NOTE: use result only when it is an ASIC/ABN search
    const bureauReport = (businessSearchValue?.type === 'search-result' && businessSearchValue?.result) ? businessSearchValue.result : data.existingApplicationBureauReport;
    const terms = calculator.getTotalInterestRateForAssetFinance(
        data.rateCard!,
        formData,
        [],
        bureauReport,
    );
    calculator.setDateValue(date.format('DD/MM/YYYY'));
    calculator.setRepaymentFrequency(paymentFrequencyValue?.type ?? 'Monthly');
    calculator.setPrinicipalValue(terms.prinicipalAmount);
    calculator.setInterestValue(terms.totalInterest);
    calculator.setloanValue(Number(loanTermValue?.type ?? '0'));
    calculator.setResidualAmount(terms.RV);
    calculator.setBrokerageAmount(terms.brokerageAmount);
    calculator.refreshUI();

    const repayment = calculator.emiAmt;
    const rst1 = calculator.getDonutChartData();
    const rst2 = calculator.getBarChartYearlyData();
    const rst3 = calculator.getBarChartQuaterlyData();
    const rst4 = calculator.getRepaymentEstimationData2(
        terms,
        paymentFrequencyValue?.type ?? 'Monthly',
        'advance',
    );
    const rst5 = calculator.getAmortisationData();
    const x: TotalPaymentBreakupDialogData = {
        repaymentEstimationData: rst4.map((rst) => ({
            amountFinance: String(rst.amountFinance),
            month24: String(rst.data.find((d) => d.loanTerm == 24)?.repayment ?? 0),
            month36: String(rst.data.find((d) => d.loanTerm == 36)?.repayment ?? 0),
            month48: String(rst.data.find((d) => d.loanTerm == 48)?.repayment ?? 0),
            month60: String(rst.data.find((d) => d.loanTerm == 60)?.repayment ?? 0),
        })),
        amortizationScheduleData: rst5.map(rst => ({
            year: String(rst.year), payment: String(rst.yearTotal), interest: String(rst.yearInterest), principal: String(rst.yearPrincipal), balance: String(rst.endingBalance),
            details: rst.childRows.map(d => ({
                monthYear: `${d.month} ${d.year}`, payment: String(d.emi), interest: String(d.interest),
                principal: String(d.pricipalPaid), balance: String(d.endingBalance)
            }))
        })),
        paymentChartData: {
            amountFinanced: rst1.pricipalAmt,
            totalInterest: terms.totalInterest,
            emiAmt: calculator.emiAmt,
            paymentFrequency: calculator.repaymentFrequency,
            principalAmt: calculator.principalAmt,
            interestAmt: calculator.interestAmt,
            totalAmt: calculator.totalAmt,
            loanTerm: calculator.loanValue, // loanValue from calculator
            lvr: terms.LVR,
            rv: terms.RV,
            brokerageAmount: calculator.brokerageAmount,
            docFee: terms.docFee,
            brokerOriginationFee: terms.brokerOriginationFee,
            applicationType: 'AssetFinance'
        },
        amortizationChartData: {
            estimatedDrawdownDate: moment(calculator.dateValue),
            annualData: rst2.map(rst => ({
                year: String(rst.year), interest: rst.yearInterest, principal: rst.yearPrincipal, balance: rst.endingBalance
            })),
            quarterlyData: rst3.map(rst => ({
                quarter: `Q${rst.quarter} ${rst.year}`, balance: rst.endingBalance, principal: rst.yearPrincipal, interest: rst.yearInterest
            }))
        },
        calculationLog: terms.calculationLog,
    };

    console.log('*************** getRepaymentEstimationData rateCard', data.rateCard);
    console.log('*************** getRepaymentEstimationData formData', formData);
    console.log('*************** getRepaymentEstimationData DonutChartData', rst1);
    console.log('*************** getRepaymentEstimationData BarChartYearlyData', rst2);
    console.log('*************** getRepaymentEstimationData BarChartQuaterlyData', rst3);
    console.log('*************** getRepaymentEstimationData RepaymentEstimationData', rst4);
    console.log('*************** getRepaymentEstimationData ArmotisationData', rst5);

    return {
        repayment,
        calculator,
        terms,
        totalPaymentBreakupDialogData: x,
    };
}

export const calculateBusinessLoanEstimation = (date: Moment, data: AppCalculatorBusinessLoanData): CalculateBusinessLoanEstimationResult => {
    const paymentFrequencyValue: PaymentFrequencyType | null = data.paymentFrequencyType ?? "Monthly";
    const loanTermType: LoanTermType | null = data.loanTermType;
    const loanTerms = Number(loanTermType ?? 0);
    const brokerageType: BrokerageSelectionType | null = data.brokerageType;
    const brokerageValue: number = Number(brokerageType ?? 0);
    const loanAmount: number = Number(data.loanAmount ?? 0);
    const docFeeFinanced = !!data?.docFeeFinanced;
    const paymentType = (data?.paymentType ?? "arrears").toLowerCase();

    const calculator = new AppCalculator();
    const formData: FormDataForBusinessFinance = {
        loanAmount: loanAmount,
        adverseOnFile: data.adverseOnFile,
        brokerage: brokerageValue,
        propertyOwner: data.propertyOwner,
        loanTerms: loanTerms,
        brokerageAmount: data.brokerageAmount,
        docFeeFinanced,
        docFee: data.docFee,
        creditRateAdjustment: data.creditRateAdjustment ?? 0, // handle creditRateAdjustment
        // ZEN-818 follow-up tier-model inputs.
        equifaxDirectorScoreAbove700: data.equifaxDirectorScoreAbove700,
        gstAgeMonths: data.gstAgeMonths,
        gstAgeMonthsWithContinuity: data.gstAgeMonthsWithContinuity,
    }

    const terms = calculator.getTotalInterestRateForBusinessFinance(
        data.rateCard!,
        formData,
        data.bureauReport,
        data.abnAgeAboveThresholdOverride
    );
    calculator.setRepaymentType(paymentType);
    calculator.setRepaymentFrequency(paymentFrequencyValue as any ?? 'Monthly');
    calculator.setPrinicipalValue(terms.prinicipalAmount);
    calculator.setInterestValue(terms.totalInterest);
    calculator.setloanValue(Number(loanTermType ?? '0'));
    calculator.setResidualAmount(terms.RV);
    calculator.setBrokeragePercent(Number(brokerageType ?? '0'));
    calculator.setBrokerageAmount(terms.brokerageAmount);
    calculator.setDateValue(date.format('DD/MM/YYYY'));
    calculator.refreshUI();

    const repayment = calculator.emiAmt;
    const rst1 = calculator.getDonutChartData();
    const rst2 = calculator.getBarChartYearlyData();
    const rst3 = calculator.getBarChartQuaterlyData();
    const rst4 = calculator.getRepaymentEstimationData2(
        terms,
        paymentFrequencyValue as any ?? 'Monthly',
        'advance',
    );
    const rst5 = calculator.getAmortisationData();

    const repaymentEstimationData = rst4.map((rst) => ({
        amountFinance: String(rst.amountFinance),
        month24: String(rst.data.find((d) => d.loanTerm == 24)?.repayment ?? 0),
        month36: String(rst.data.find((d) => d.loanTerm == 36)?.repayment ?? 0),
        month48: String(rst.data.find((d) => d.loanTerm == 48)?.repayment ?? 0),
        month60: String(rst.data.find((d) => d.loanTerm == 60)?.repayment ?? 0),
    }));
    const amortizationScheduleData = rst5.map(rst => ({
        year: String(rst.year), payment: String(rst.yearTotal), interest: String(rst.yearInterest), principal: String(rst.yearPrincipal), balance: String(rst.endingBalance),
        details: rst.childRows.map(d => ({
            monthYear: `${d.month} ${d.year}`, payment: String(d.emi), interest: String(d.interest),
            principal: String(d.pricipalPaid), balance: String(d.endingBalance)
        }))
    }));
    const paymentChartData = {
        amountFinanced: rst1.pricipalAmt,
        totalInterest: terms.totalInterest,
        emiAmt: calculator.emiAmt,
        paymentFrequency: calculator.repaymentFrequency,
        principalAmt: calculator.principalAmt,
        interestAmt: calculator.interestAmt,
        totalAmt: calculator.totalAmt,
        loanTerm: calculator.loanValue, // loanValue from calculator
        lvr: terms.LVR,
        rv: terms.RV,
        brokerageAmount: terms.brokerageAmount,
        docFee: terms.docFee,
        brokerOriginationFee: terms.brokerOriginationFee,
        applicationType: 'BusinessLoans'
    };
    const amortizationChartData = {
        estimatedDrawdownDate: moment(calculator.dateValue),
        annualData: rst2.map(rst => ({
            year: String(rst.year), interest: rst.yearInterest, principal: rst.yearPrincipal, balance: rst.endingBalance
        })),
        quarterlyData: rst3.map(rst => ({
            quarter: `Q${rst.quarter} ${rst.year}`, balance: rst.endingBalance, principal: rst.yearPrincipal, interest: rst.yearInterest
        }))
    };

    return {
        repayment,
        calculator,
        terms,
        totalPaymentBreakupDialogData: {
            repaymentEstimationData,
            amortizationScheduleData,
            paymentChartData,
            amortizationChartData,
            calculationLog: terms.calculationLog,
            // isBusinessLoan: true,
            // hideBrokerage: !!data.hideBrokerage,
        },
    }
}

export const calculateBusinessOverdraftEstimation = (date: Moment, data: AppCalculatorBusinessOverdraftData): CalculateBusinessOverdraftEstimationResult => {
    const calculator = new AppCalculator();
    const formData: FormDataForBusinessOverdraft = {
        facilityLimit: data.facilityLimit,
        loanTerms: Number(data.loanTermType ?? 0),
        propertyOwner: data.propertyOwner,
        adverseOnFile: data.adverseOnFile,
        docFee: data.docFee,
        brokerageAmount: data.brokerageAmount,
        creditRateAdjustment: data.creditRateAdjustment ?? 0,
        // ZEN-818 follow-up tier-model inputs.
        equifaxDirectorScoreAbove700: data.equifaxDirectorScoreAbove700,
        gstAgeMonths: data.gstAgeMonths,
        gstAgeMonthsWithContinuity: data.gstAgeMonthsWithContinuity,
        additionalCommission: data.additionalCommission,
    };
    calculator.setRepaymentFrequency('Monthly');
    calculator.setloanValue(Number(data.loanTermType ?? 0));
    calculator.refreshUI();

    const term = calculator.getTotalInterestRateForBusinessOverdraft(data.rateCard!, formData)

    // Calculate anticipated monthly interest and cost if anticipated drawdown amount is provided
    if (data.anticipatedDrawdownAmount != null && data.anticipatedDrawdownAmount > 0) {
      const rbaRate = data.rateCard?.rbaRate ?? 0;
      const margin = term.margin;

      // Anticipated Monthly Interest = (RBA + Margin) * anticipated drawdown amount / 12
      const anticipatedMonthlyInterest = ((rbaRate + margin) / 100) * data.anticipatedDrawdownAmount / 12;

      // Anticipated Monthly Cost = Anticipated Monthly Interest + Monthly Facility Fee
      const anticipatedMonthlyCost = anticipatedMonthlyInterest + term.monthlyFacilityFee;

      term.anticipatedMonthlyInterest = anticipatedMonthlyInterest;
      term.anticipatedMonthlyCost = anticipatedMonthlyCost;
  }

    return {
      calculator,
      term,
    };
}

/**
 * Unified public API for Business Overdraft pricing calculation.
 *
 * Always runs `calculateBusinessOverdraftEstimation` first so the rate-card-derived brokerage%
 * (which includes the `BrokerShareFacilityFee` component) is computed correctly.
 * For variation loan purposes (Limit Increase / Limit Decrease / Renewal) it then
 * overrides `brokerageDollar` and adds the early termination fee via the internal
 * `calculateBusinessOverdraftVariationFees` helper.
 *
 * Prefer this function over calling `calculateBusinessOverdraftEstimation` directly.
 */
export const calculateBusinessOverdraftFees = (
  date: Moment,
  data: AppCalculatorBusinessOverdraftData,
): CalculateBusinessOverdraftEstimationResult => {
  const result = calculateBusinessOverdraftEstimation(date, data);

  // Step 2: for variation loan purposes, override brokerageDollar and add ETF
  if (isBusinessOverdraftVariationPurpose(data.loanPurpose)) {
    const variation = calculateBusinessOverdraftVariationFees(
      data.activeAccountDetails ?? null,
      data.loanPurpose ?? null,
      data.termRenewal ?? false,
      data.facilityLimit,
      result.term.brokerage,
    );
    result.term.brokerageDollar = variation.brokerageDollar ?? result.term.brokerageDollar;
    result.term.brokerage = variation.brokerage ?? result.term.brokerage;
    result.earlyTerminationFee = variation.earlyTerminationFee;
    result.earlyTerminationFeePercent = variation.earlyTerminationFeePercent;
  }

  return result;
};

export const isAddressEquals = (address1: any, address2: any) => {
    if (_.isEqual(address1, address2)) {
        return true;
    } else {
        return false;
    }
}

